import * as Cesium from 'cesium'
import { BooleanEnum, BtnCallBackLoading, BtnLoading, CoordinatesType, DEVICE_TYPE, FLY_TO_WAYLINE_MODEL_TYPE, FollowBadType, FollowType, locationPosition, PointEnum, SEQUENCE, TEMPLATE_TYPE, WaylineActionType, waylineFont, WaylineMapType, WaylineSmoothType, WaylineTypeMap } from '../types/wayline'
import { meanSeaLevel } from 'egm96-universal'
import { uuidv4 } from './uuid'
import store from '/@/store'
import { DEVICE_CAMERA_DIR, disabledAction, DRONE_WAYLINE_TYPE, PAYLOAD_TRY_TYPE } from '../types/device'
import { GetNewPoint, GetPoint } from './kml'
import { conversion, state, viewer } from '../hooks/Cesium'
import { nextTick, reactive, ref } from 'vue'
import emitter from '../event-bus'
import dayjs from 'dayjs'
import { PolylineDirectionMaterial } from '../hooks/Cesium/polyline-direction-material'
import { CameraFrustumVisualizer } from '../hooks/Cesium/draw-flight-simulation'
import { downloadWaylineFile } from '../api/wayline'
import JSZip from 'jszip'
import DJIWaylineTools from './kmlUtil'
import { currentPlanarRouteSettingData } from './kmlPlanar'
import { currentStripRouteSettingData } from './kmlStrip'
export interface Waypoint {
  longitude: number
  latitude: number
  height: number // 海拔高度（米）
}

export interface TakeOffPoint {
  longitude: number
  latitude: number
  altitude: number // 相对起飞点高度或绝对高度，根据数据来源
}

export interface ParsedWaylineData {
  takeOffPoint: TakeOffPoint | null
  waypoints: Waypoint[]
}

export const currentSettingData = reactive<any>({
  payloadLensIndex: [
    'wide',
    'zoom'
  ],
  imageFormat: [
    'wide',
    'zoom'
  ],
  globalTransitionalSpeed: 10,
  flyToWaylineMode: 'safely',
  takeOffSecurityHeight: 20,
  heightMode: 'EGM96',
  globalRTHHeight: 100,
  globalHeight: 100,
  autoFlightSpeed: 10,
  waypointTurnMode: 'coordinateTurn',
  waypointHeadingMode: 'followWayline',
  gimbalPitchMode: 'manual',
  finishAction: 'goHome',
  orientedPhotoMode: false
})
export const globalType = ref(0)
export const headTop = ref(0)
export const activeIndex = ref(null)
export const activeBtnIndex = ref('')
export const addActionIndex = ref(null)
export const isSave = ref(false)
export const isEdit = ref(false)
export const isMove = ref(false)
export const isMoveAlt = ref(false)
export const moveStyle = ref<{top:number|string, left:number|string}>({ top: 0, left: 0 })
export const cellAction = reactive({
  type: '',
  selectIndex: 0,
  actionParams: {
    useGlobalPayloadLensIndex: 1,
    fileSuffix: '',
    payloadLensIndex: [],
    actionTriggerParam: 0,
    hoverTime: 0,
    aircraftHeading: 0,
    gimbalYawRotateAngle: 0,
    gimbalPitchRotateAngle: 0,
    focalLength: 2,
    directoryName: '',
    actionList: [],
    uuid: '',
    searchlightOperateType: 1,
    searchlightBrightness: 100,
    speakerPlayType: waylineFont.TTS,
    megaphoneOperateVolume: 100,
    megaphoneOperateLoop: 0,
    ttsParam: {
      text: '喊话示例',
      role: 'male',
      language: 'CN',
      volume: 100,
      speed: 50
    }
  },
  actionEventTitle: ''
})
export const letCell = reactive({
  psdkName: '',
  psdkValue: 1,
  algorithmParam: '',
  warnType: '0',
  speakerPlayType: 'TTS',
  speakerPlayContentId: 0,
  speakerPlayContent: '',
  speakerPlayLanguage: 0,
  speakerPlayVolume: 50,
  speakerPlayMode: 0,
  speakerPlayEndWaypoint: 1
})
export const showTip = reactive({
  data: {
    show: !1,
    text: 'CZI_YYYYMMDDhhmm_XXX_',
    inputName: ''
  }
})
export const peFlag = reactive({
  arRecordSwitch: false
})
export const waypoint = reactive({
  data: {
    waypointHeadingAngle: 0,
    waypointHeadingPathMode: FollowBadType.FOLLOW_BAD_ARC
  }
})
const addHandler = ref()
export const setAddEvent = () => {
  removeAddEvent()
  addHandler.value = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas)
  addHandler.value.setInputAction((event) => {
    const ray = viewer.camera.getPickRay(event.position)
    if (!ray) return
    const cartesian = viewer.scene.globe.pick(ray, viewer.scene)
    const rightPosition = conversion(cartesian, viewer)
    handleAddBeforePoint(rightPosition, event.position)
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK, Cesium.KeyboardEventModifier.ALT)
  addHandler.value.setInputAction((event) => {
    const ray = viewer.camera.getPickRay(event.position)
    if (!ray) return
    const cartesian = viewer.scene.globe.pick(ray, viewer.scene)
    const rightPosition = conversion(cartesian, viewer)
    handleAddAfterPoint(rightPosition, event.position)
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK, Cesium.KeyboardEventModifier.CTRL)
}

export const removeAddEvent = () => {
  if (addHandler.value) {
    addHandler.value.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK, Cesium.KeyboardEventModifier.ALT)
    addHandler.value.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK, Cesium.KeyboardEventModifier.CTRL)
    addHandler.value.destroy()
    addHandler.value = null
  }
}
export function setKeyMove (keyCode:number, cb?:Function) {
  if (!state.drawRouteTool?._frustumTool) { return }
  setHpr(state.drawRouteTool._frustumTool)
  const cartesian = Cesium.Cartographic.fromCartesian(state.drawRouteTool?._frustumTool.position)
  const longitude = Cesium.Math.toDegrees(cartesian.longitude)
  const latitude = Cesium.Math.toDegrees(cartesian.latitude)
  let height = cartesian.height
  if (keyCode === 32) {
    // ESC
    if (cb)cb()
  } else if (keyCode === 27) {
    // 空格
    if (cb)cb()
  } else if (keyCode === 87 || keyCode === 83 || keyCode === 65 || keyCode === 68) {
    const p = {
      87: 180,
      83: 0,
      65: 90,
      68: -90
    }
    const posHead = state.drawRouteTool?._frustumTool.getPosByHeading(globalType.value + p[keyCode], 2)
    const posCartesian = Cesium.Cartographic.fromCartesian(posHead)
    posCartesian.height = cartesian.height
    const cartographic = viewer.scene.globe.ellipsoid.cartographicToCartesian(new Cesium.Cartographic(posCartesian.longitude, posCartesian.latitude, posCartesian.height))
    state.drawRouteTool?._frustumTool.update(cartographic, globalType.value, headTop.value)
    aircraftChange(state.drawRouteTool?.getStartingPoint(), state.drawRouteTool._dataSource)
    // emitter.emit("setCameraMapView", {
    //     position: state.drawRouteTool?._frustumTool.position,
    //     heading: globalType.value,
    //     pitch: (headTop.value - 90) * -1
    // })
    setLookAtTransform()
  }
  if (keyCode === 90 || keyCode === 67) {
    height += {
      90: -1,
      67: 1
    }[keyCode]
    const d = Cesium.Cartesian3.fromDegrees(longitude, latitude, height)
    state.drawRouteTool?._frustumTool.update(d, globalType.value, headTop.value)
    aircraftChange(state.drawRouteTool?.getStartingPoint(), state.drawRouteTool._dataSource)
    store.state.isUseGlobalHeight = 0
    // emitter.emit("setCameraMapView", {
    //     position: state.drawRouteTool?._frustumTool.position,
    //     heading: globalType.value,
    //     pitch: (headTop.value - 90) * -1
    // }),
    setLookAtTransform()
  }
  if (keyCode === 87 || keyCode === 83 || keyCode === 65 || keyCode === 68 || keyCode === 90 || keyCode === 67) {
    emitter.emit('aircraftPositionChange', state.drawRouteTool?._frustumTool.position)
  }
  if (keyCode === 81) {
    globalType.value -= 0.5
    if (globalType.value > 180) {
      globalType.value -= 360
    } else {
      if (globalType.value < -180) {
        globalType.value += 360
      }
    }
    setCameraMapAircraft(globalType.value, headTop.value)
  } else {
    if (keyCode === 69) {
      globalType.value += 0.5
      if (globalType.value > 180) {
        globalType.value -= 360
      } else {
        if (globalType.value < -180) {
          globalType.value += 360
        }
      }
      setCameraMapAircraft(globalType.value, headTop.value)
    }
  }
}
function setCameraMapAircraft (heading, pitch) {
  if (store.state.isLockMode) {
    state.drawRouteTool?._frustumTool.update(state.drawRouteTool?._frustumTool.position, heading, pitch)
    emitter.emit('setCameraMapView', {
      position: state.drawRouteTool?._frustumTool.position,
      heading: heading,
      pitch: (pitch - 90) * -1
    })
  } else {
    state.drawRouteTool?._frustumTool.updateAircraft(state.drawRouteTool?._frustumTool.position, heading, pitch)
    state.drawRouteTool?._frustumTool.update(state.drawRouteTool?._frustumTool.position, heading, pitch)
    // store.state.setViewerHeading(heading)
    if (store.state.viewIsLock) {
      const distance = Cesium.Cartesian3.distance(state.drawRouteTool?._frustumTool.position, viewer.camera.position)
      viewer.camera.lookAt(state.drawRouteTool?._frustumTool.position, new Cesium.HeadingPitchRange(Cesium.Math.toRadians(heading), viewer.camera.pitch, distance))
      viewer.camera.lookAtTransform(Cesium.Matrix4.IDENTITY)
    }
  }
}

export function setHpr (frustum) {
  globalType.value = frustum.heading
  headTop.value = frustum.pitch
}

/** 获取点的asl，hae
 * @param {Cesium.Cartesian3} position 笛卡尔坐标点
 * @returns {object} 返回对象包含 asl 和 hae 值，单位均为米。asl 为椭球高，hae 为大地高（椭球高 + 大地水准面差距）
 */
export function getAltPoint (position: Cesium.Cartesian3) {
  if (!position) return { asl: 0, hae: 0 }
  const cartographic = Cesium.Cartographic.fromCartesian(position)
  const obj = {
    asl: 0,
    hae: 0
  }
  obj.asl = Number(cartographic.height.toFixed(1))
  const te = Cesium.Math.toDegrees(cartographic.longitude)
  const se = Cesium.Math.toDegrees(cartographic.latitude)
  const ae = meanSeaLevel(se, te)
  obj.hae = Number((cartographic.height + ae).toFixed(1))
  return obj
}

/**
 * 根据经度、纬度和 asl（椭球高）获取笛卡尔坐标点
 * @param {number} longitudeDegrees 经度（度）
 * @param {number} latitudeDegrees 纬度（度）
 * @param {number} asl 椭球高（米），对应 getAltPoint 返回的 asl 值
 * @returns {Cesium.Cartesian3}
 */
export function getPointByAsl (longitudeDegrees:number, latitudeDegrees:number, asl:number) {
  if (longitudeDegrees === undefined || latitudeDegrees === undefined || asl === undefined) {
    throw new Error('Missing required parameters: longitudeDegrees, latitudeDegrees, asl')
  }
  const cartographic = Cesium.Cartographic.fromDegrees(longitudeDegrees, latitudeDegrees, asl)
  return Cesium.Ellipsoid.WGS84.cartographicToCartesian(cartographic)
}

/**
 * 根据经度、纬度和 hae（椭球高 + 大地水准面差距）获取笛卡尔坐标点
 * @param {number} longitudeDegrees 经度（度）
 * @param {number} latitudeDegrees 纬度（度）
 * @param {number} hae hae值（米），对应 getAltPoint 返回的 hae 值
 * @returns {Cesium.Cartesian3}
 */
export function getPointByHae (longitudeDegrees:number, latitudeDegrees:number, hae:number) {
  if (longitudeDegrees === undefined || latitudeDegrees === undefined || hae === undefined) {
    throw new Error('Missing required parameters: longitudeDegrees, latitudeDegrees, hae')
  }
  // 获取大地水准面差距 N
  const N = meanSeaLevel(latitudeDegrees, longitudeDegrees)
  // 根据 hae = 椭球高 + N 解算椭球高
  const ellipsoidalHeight = hae - N
  const cartographic = Cesium.Cartographic.fromDegrees(longitudeDegrees, latitudeDegrees, ellipsoidalHeight)
  return Cesium.Ellipsoid.WGS84.cartographicToCartesian(cartographic)
}

export function setLookAtTransform () {
  let distance = Cesium.Cartesian3.distance(state.drawRouteTool?._frustumTool.position, viewer.camera.position)
  distance = distance > 4e3 ? 4e3 : distance
  store.state.viewIsLock ? viewer.camera.lookAt(state.drawRouteTool?._frustumTool.position, new Cesium.HeadingPitchRange(Cesium.Math.toRadians(globalType.value - 90), viewer.camera.pitch, distance)) : viewer.camera.lookAt(state.drawRouteTool?._frustumTool?.position, new Cesium.HeadingPitchRange(viewer.camera.heading, viewer.camera.pitch, distance))
  viewer.camera.lookAtTransform(Cesium.Matrix4.IDENTITY)
}
export function aircraftChange (position, viewer) {
  const entities = viewer.entities.getById('aircraft_position_dashLine')
  const cartesian = setPostion(position, viewer)
  entities.position = cartesian
  entities.polyline.positions = new Cesium.CallbackProperty(() => [cartesian, position], !1)
  entities.polyline.material = new Cesium.PolylineDashMaterialProperty({
    color: new Cesium.Color(1, 1, 0, 1),
    gapColor: new Cesium.Color(0, 0, 0, 0),
    dashLength: 10
  })
  entities.point.color = new Cesium.Color(1, 1, 0, 1)
}
function setPostion (position, viewer) {
  const cartesian = Cesium.Cartographic.fromCartesian(position)
  const longitude = Cesium.Math.toDegrees(cartesian.longitude)
  const latitude = Cesium.Math.toDegrees(cartesian.latitude)
  const height = viewer.scene.globe.getHeight(Cesium.Cartographic.fromDegrees(longitude, latitude, 0))
  return Cesium.Cartesian3.fromDegrees(longitude, latitude, height)
}
export function setSettingData (kmlData, formData) {
  const folder = kmlData.Document.Folder[0]
  const L = folder?.globalWaypointHeadingParam
  const S = folder
  const G = folder?.payloadParam

  // 从KML数据中提取各种配置参数
  currentSettingData.flyToWaylineMode = kmlData.Document.missionConfig.flyToWaylineMode
  currentSettingData.takeOffSecurityHeight = kmlData.Document.missionConfig.takeOffSecurityHeight * 1 ?? 20
  currentSettingData.heightMode = folder.waylineCoordinateSysParam.heightMode
  currentSettingData.globalRTHHeight = kmlData.Document.missionConfig.globalRTHHeight * 1 ?? 100
  currentSettingData.globalHeight = folder.globalHeight * 1 ?? 100
  currentSettingData.autoFlightSpeed = folder.autoFlightSpeed * 1 ?? 10

  // 处理航点转弯模式
  let mode = folder.globalWaypointTurnMode
  if (mode === WaylineSmoothType.TO_POINT_AND_PASS_WITH_CONTINUITY_CURVATURE &&
    folder.globalUseStraightLine === 1) {
    mode = WaylineSmoothType.SMOOTH_OVER_POINTS
  }
  currentSettingData.waypointTurnMode = mode

  // 设置航点航向模式（使用L变量）
  currentSettingData.waypointHeadingMode = L?.waypointHeadingMode ?? FollowType.FOLLOW_WAYLINE

  // 设置云台俯仰模式和结束动作
  currentSettingData.gimbalPitchMode = folder.gimbalPitchMode
  currentSettingData.finishAction = kmlData.Document.missionConfig.finishAction

  // 处理图像格式设置（使用S和G变量）
  const payloadList = DEVICE_CAMERA_DIR[formData.payload_types[0]] || []
  currentSettingData.imageFormat = G?.imageFormat
    ? folder.payloadParam.imageFormat.split(',')
    : payloadList

  // 设置全局过渡速度
  currentSettingData.globalTransitionalSpeed = kmlData.Document.missionConfig.globalTransitionalSpeed

  // 重置保存状态
  isSave.value = false
}
function getLatLng (position, viewer) {
  const latLng = Cesium.Cartographic.fromCartesian(position)
  const longitude = Cesium.Math.toDegrees(latLng.longitude)
  const latitude = Cesium.Math.toDegrees(latLng.latitude)
  const globelHeight = latLng.height
  return Cesium.Cartesian3.fromDegrees(longitude, latitude, globelHeight)
}
export function getHeight (type?:string) {
  let setting = currentSettingData
  if (type === 'planar') {
    setting = currentPlanarRouteSettingData
  } else if (type === 'strip') {
    setting = currentStripRouteSettingData
  }
  let height = 0
  const cartesian = Cesium.Cartographic.fromCartesian(store.state.routeStartPosition)
  if (setting.heightMode === WaylineMapType.EGM96) {
    height = Number(setting.globalHeight)
  } else if (setting.heightMode === WaylineMapType.RELATIVE_TO_START_POINT) {
    height = Number(setting.globalHeight) + cartesian.height
  } else if (setting.heightMode === WaylineMapType.ABOVE_GROUND_LEVEL) {
    const position = getLatLng(store.state.routeStartPosition, viewer)
    const location = Cesium.Cartographic.fromCartesian(position)
    height = Number(setting.globalHeight) + location.height
  }
  return height
}

export const handleUpdateHeight = (value, type, current, view) => {
  const viewers = view || viewer
  const currentCartesian = current
  if (!currentCartesian) return
  const currentCartographic = Cesium.Cartographic.fromCartesian(currentCartesian)
  const longitude = Cesium.Math.toDegrees(currentCartographic.longitude)
  const latitude = Cesium.Math.toDegrees(currentCartographic.latitude)
  // 根据用户输入的模式，计算出绝对椭球高（ASL）
  let absoluteHeight = 0
  const inputHeight = Number(value)
  switch (type) {
    case 'ALTheight': { // 相对起飞点高度 -> 转成 ASL
      const startPos = store.state.routeStartPosition
      if (startPos) {
        const startCartographic = Cesium.Cartographic.fromCartesian(startPos)
        absoluteHeight = Number(value) + startCartographic.height
      } else {
        absoluteHeight = Number(value)
      }
      break
    }
    case 'ASLheight': // 直接是 ASL
      absoluteHeight = inputHeight
      break
    case 'height': { // 离地高度 -> 转成 ASL
      const groundHeight = viewers.scene.globe.getHeight(
        Cesium.Cartographic.fromDegrees(longitude, latitude, 0)
      )
      absoluteHeight = inputHeight + (groundHeight || 0)
      break
    }
    default:
      return
  }

  const newCartographic = Cesium.Cartographic.fromDegrees(longitude, latitude, absoluteHeight)
  const newCartesian = Cesium.Ellipsoid.WGS84.cartographicToCartesian(newCartographic)
  return setVal(newCartesian, value, type, viewers)
}

function setVal (cartesian, value, type, view) {
  const cartographic = Cesium.Cartographic.fromCartesian(cartesian)
  let seaLevelHeightValue = 0
  let altitudeHeightValue = 0
  let relativeHeightValue = 0
  if (type === 'ALTheight') {
    altitudeHeightValue = value
  } else if (type === 'ASLheight') {
    seaLevelHeightValue = value
  } else {
    relativeHeightValue = value
  }

  // 更新 ASL（海拔高度）显示
  if (type !== 'ASLheight') {
    seaLevelHeightValue = cartographic.height.toFixed(1)
  }

  const startCartesian = store.state.routeStartPosition
  if (startCartesian) {
    const startCartographic = Cesium.Cartographic.fromCartesian(startCartesian)
    if (type !== 'ALTheight') {
      altitudeHeightValue = (cartographic.height - startCartographic.height).toFixed(1)
    }
  } else {
    if (type !== 'ALTheight') {
      altitudeHeightValue = cartographic.height.toFixed(1)
    }
  }

  const lonDeg = Cesium.Math.toDegrees(cartographic.longitude)
  const latDeg = Cesium.Math.toDegrees(cartographic.latitude)
  const groundHeight = view.scene.globe.getHeight(
    Cesium.Cartographic.fromDegrees(lonDeg, latDeg, 0)
  )

  if (type !== 'height') {
    relativeHeightValue = (cartographic.height - (groundHeight || 0)).toFixed(1)
  }

  // 返回统一的高度对象（供外部调用）
  return {
    ALTheight: altitudeHeightValue,
    ASLheight: seaLevelHeightValue,
    height: relativeHeightValue
  }
}

/** 获取WGS84点 */
export function getEllipsoidCartesian (point) {
  const cartographic = Cesium.Cartographic.fromDegrees(point.lng * 1, point.lat * 1, point.h * 1)
  return Cesium.Ellipsoid.WGS84.cartographicToCartesian(cartographic)
}

/** 获取起飞点起飞航线点
 * @param viewer 地图实例
 * @param startPoint 起飞点笛卡尔坐标
 * @param firstPoint 第一个航线点笛卡尔坐标
 * @param options 配置参数对象，包含起飞方式（flyToWaylineMode）和安全起飞高度（takeOffSecurityHeight）
 */
export function getSetFlyToFirstPoint (viewer:Cesium.Viewer, start:{longitude:number, latitude:number, height:number}, first:{longitude:number, latitude:number, height:number}, options:{flyToWaylineMode:FLY_TO_WAYLINE_MODEL_TYPE, takeOffSecurityHeight:number}) {
  // viewer, startPoint, firstPoint, options
  /** 获取起飞点到第一个航点的笛卡尔坐标 */
  const startFirst = Cesium.Cartesian3.fromDegrees(start.longitude, start.latitude, start.height)
  let points = [] as any

  if (options.flyToWaylineMode === FLY_TO_WAYLINE_MODEL_TYPE.SAFELY) {
    /** 垂直爬坡 */
    /** 起飞点-航点航线起始点高度的笛卡尔坐标 */
    const secondPoint = Cesium.Cartesian3.fromDegrees(start.longitude, start.latitude, first.height)
    points = [startFirst, secondPoint]
  } else if (options.flyToWaylineMode === FLY_TO_WAYLINE_MODEL_TYPE.POINT_TO_POINT) {
    /** 倾斜爬升 */
    /** 起飞点-安全起飞高度的笛卡尔坐标
     * @description 安全起飞点-相对起飞点高度+起飞点原始高度
     */
    const secondPoint = Cesium.Cartesian3.fromDegrees(start.longitude, start.latitude, start.height + options.takeOffSecurityHeight)
    points = [startFirst, secondPoint]
  }
  return points
}

/** 获取高度
 * @param mapType 航线高度类型
 * @param point 航线点对象，包含高度、经纬度坐标和椭球面高度
 * @param startHeight 起飞点椭球面高度
 */
export function getHeightPoint (mapType:string, point: { height: number; Point: { coordinates: { latitude: number; longitude: number } }; ellipsoidHeight: number }, startHeight:number) {
  let height = 0
  if (mapType === WaylineMapType.EGM96) {
    height = point.height * 1
  } else if (mapType === WaylineMapType.RELATIVE_TO_START_POINT) {
    height = point.height * 1 + startHeight
  } else if (mapType === WaylineMapType.ABOVE_GROUND_LEVEL) {
    const offset = meanSeaLevel(point.Point.coordinates.latitude * 1, point.Point.coordinates.longitude * 1)
    height = point.ellipsoidHeight * 1 - offset
  }
  return height
}
/** 根据两点获取对应角度
 * @param frame 后一点笛卡尔坐标
 * @param target 前一点笛卡尔坐标
 */
export function getPointPosition (frame:Cesium.Cartesian3, target:Cesium.Cartesian3) {
  const eastNorthUpFrame = Cesium.Transforms.eastNorthUpToFixedFrame(frame)
  const inverseFrame = Cesium.Matrix4.inverse(eastNorthUpFrame, new Cesium.Matrix4())
  const localPoint = Cesium.Matrix4.multiplyByPoint(inverseFrame, target, new Cesium.Cartesian3())
  const heading = Math.atan2(localPoint.x, localPoint.y)
  const distance = Cesium.Cartesian3.magnitude(localPoint)
  const pitch = Math.asin(localPoint.z / distance)
  return {
    h: heading,
    distance: distance,
    p: pitch,
    latitude: 0
  }
}

export function setRouterListParams (actionList, key, pointArray) {
  actionList.forEach(item => {
    const action = Array.isArray(item.action) ? item.action[0] : item.action
    const useGlobal = action.actionActuatorFuncParam.useGlobalPayloadLensIndex
    pointArray[item.actionGroupStartIndex].eventList.push({
      type: item.actionTrigger.actionTriggerType,
      actionTriggerParam: item.actionTrigger.actionTriggerParam,
      useGlobalPayloadLensIndex: useGlobal,
      payloadLensIndex: useGlobal === 0 ? action.actionActuatorFuncParam.payloadLensIndex.split(',') : [],
      uuid: uuidv4()
    })
    pointArray[item.actionGroupEndIndex].eventList.push({
      type: 'multipleEnd',
      uuid: uuidv4()
    })
  })
}

export function handleHeadLat (points) {
  let maxLng = points[0][0]
  let maxLat = points[0][1]
  for (let i = 0; i < points.length; i++) {
    if (maxLng < points[i][0]) maxLng = points[i][0]
    if (maxLat < points[i][1]) maxLat = points[i][1]
  }
  return {
    lng: maxLng * 1,
    lat: maxLat * 1
  }
}

export function handleHeadLng (points) {
  let minLng = 99999999999
  let minLat = 99999999999
  for (let i = 0; i < points.length; i++) {
    if (minLng > points[i][0]) (minLng = points[i][0])
    if (minLat > points[i][1]) (minLat = points[i][1])
  }
  return {
    lng: minLng * 1,
    lat: minLat * 1
  }
}

export function mergeArRecordActions (actions) {
  const mergedActions = []
  let skipIndex = -1
  actions.forEach((action, index) => {
    if (skipIndex === index) { return }
    let mergedAction
    switch (action.customActionType) {
      case DEVICE_TYPE.START_AR_RECORD:
        mergedAction = {
          type: action.customActionType,
          uuid: uuidv4(),
          actionList: [actions[index], actions[index + 1]]
        }
        skipIndex = index + 1
        break
      case DEVICE_TYPE.STOP_AR_RECORD:
        mergedAction = {
          type: action.customActionType,
          uuid: uuidv4()
        }
        break
      default:
        mergedAction = action
        break
    }
    mergedActions.push(mergedAction)
  })
  return mergedActions
}

function buildCustomAction (action) {
  let customAction
  switch (action.customActionType) {
    case DEVICE_TYPE.START_AI:
      {
        const algorithmConfig = action.customActionParam.algorithmParamConfig
        const arr = algorithmConfig.split('-')
        customAction = {
          uuid: uuidv4(),
          type: action.customActionType,
          customActionParam: {
            warnType: action.customActionParam.warnType,
            algorithmParam: action.customActionParam.algorithmParam,
            aiType: Number(arr[0]),
            aiEvent: arr[arr.length - 1],
            algorithmParamConfig: algorithmConfig !== '' ? algorithmConfig.split(',') : []
          }
        }
      }
      break
    case DEVICE_TYPE.STOP_AI:
      {
        const algorithmConfig = action.customActionParam.algorithmParamConfig
        customAction = {
          uuid: uuidv4(),
          type: action.customActionType,
          customActionParam: {
            warnType: action.customActionParam.warnType,
            algorithmParam: action.customActionParam.algorithmParam,
            algorithmParamConfig: algorithmConfig !== '' ? algorithmConfig.split(',') : []
          }
        }
      }
      break
    case DEVICE_TYPE.START_MP20_LIGHTFLASH:
      customAction = {
        uuid: uuidv4(),
        type: action.customActionType,
        customActionParam: {
          psdkValue: action.customActionParam.psdkValue,
          psdkName: action.customActionParam.psdkName,
          psdkIndex: action.customActionParam.psdkIndex
        }
      }
      break
    case DEVICE_TYPE.STOP_MP20_LIGHTFLASH:
      customAction = {
        uuid: uuidv4(),
        type: action.customActionType,
        customActionParam: {
          psdkValue: action.customActionParam.psdkValue,
          psdkName: action.customActionParam.psdkName,
          psdkIndex: action.customActionParam.psdkIndex
        }
      }
      break
    case DEVICE_TYPE.START_SPEAKER_PLAY:
      customAction = {
        uuid: uuidv4(),
        type: action.customActionType,
        customActionParam: {
          psdkName: action.customActionParam.psdkName,
          psdkIndex: action.customActionParam.psdkIndex,
          speakerPlayType: action.customActionParam.speakerPlayType,
          speakerPlayContentId: action.customActionParam.speakerPlayContentId,
          speakerPlayContent: '',
          speakerPlayLanguage: action.customActionParam.speakerPlayLanguage,
          speakerPlayVolume: action.customActionParam.speakerPlayVolume,
          speakerPlayMode: action.customActionParam.speakerPlayMode,
          speakerPlayEndWaypoint: action.customActionParam.speakerPlayEndWaypoint
        }
      }
  }
  return customAction
}

export function actionSet (actions) {
  let processedActions = []
  actions.forEach(row => {
    let ttsText, ttsRole, ttsLanguage, ttsVolume, ttsSpeed
    const params = row.actionActuatorFuncParam
    if (params) {
      if (!params.fileSuffix) {
        params.fileSuffix = ''
      }
      if (!params.payloadLensIndex) {
        params.payloadLensIndex = ''
      }
    }
    let currentAction = {}
    switch (row.actionActuatorFunc) {
      case WaylineActionType.START_RECORD:
      case WaylineActionType.TAKE_PHOTO:
        currentAction = {
          fileSuffix: params.fileSuffix,
          payloadLensIndex: params.payloadLensIndex.split(','),
          payloadPositionIndex: params.payloadPositionIndex,
          useGlobalPayloadLensIndex: params.useGlobalPayloadLensIndex
        }
        break
      case WaylineActionType.STOP_RECORD:
        currentAction = {
          payloadPositionIndex: params.payloadPositionIndex
        }
        break
      case WaylineActionType.HOVER:
        currentAction = {
          hoverTime: params.hoverTime
        }
        break
      case WaylineActionType.ROTATE_YAW:
        currentAction = {
          aircraftHeading: params.aircraftHeading,
          aircraftPathMode: params.aircraftPathMode
        }
        break
      case WaylineActionType.GIMBAL_ROTATE:
        if (params.gimbalYawRotateEnable * 1) {
          currentAction = {
            gimbalYawRotateAngle: params.gimbalYawRotateAngle
          }
        } else {
          if (params.gimbalPitchRotateEnable * 1) {
            currentAction = {
              gimbalPitchRotateAngle: params.gimbalPitchRotateAngle
            }
          }
        }
        break
      case WaylineActionType.GIMBAL_ROTATE_PITCH:
        currentAction = {
          gimbalPitchRotateAngle: params.gimbalPitchRotateAngle
        }
        break
      case WaylineActionType.ZOOM:
        currentAction = {
          focalLength: Number((params.focalLength / 24).toFixed(0)),
          isUseFocalFactor: params.isUseFocalFactor,
          payloadPositionIndex: params.payloadPositionIndex
        }
        break
      case WaylineActionType.CUSTOM_DIRNAME:
        currentAction = {
          directoryName: params.directoryName,
          payloadPositionIndex: params.payloadPositionIndex
        }
        break
      case WaylineActionType.PANOSHOT:
        currentAction = {}
        break
      case WaylineActionType.SEARCH_LIGHT:
        currentAction = {
          searchlightOperateType: params.searchlightOperateType,
          searchlightBrightness: params.searchlightBrightness
        }
        break
      case WaylineActionType.MEGAPHONE:
        currentAction = {
          speakerPlayType: waylineFont.TTS,
          actionUUID: params.actionUUID ?? '',
          megaphoneOperateType: params.megaphoneOperateType,
          megaphoneOperateVolume: params.megaphoneOperateVolume,
          megaphoneOperateLoop: params.megaphoneOperateLoop,
          megaphoneFileOriginalName: params.megaphoneFileOriginalName,
          megaphoneOperateFilePath: (params == null ? undefined : params.megaphoneOperateFilePath) ?? '',
          megaphoneFileName: (params == null ? undefined : params.megaphoneFileName) ?? '',
          megaphoneFileMd5: (params == null ? undefined : params.megaphoneFileMd5) ?? '',
          megaphoneFileBitrate: params.megaphoneFileBitrate
        }
        if (params.megaphoneOperateType === 0 && !(params != null && params.megaphoneFileName)) {
          currentAction.speakerPlayType = waylineFont.TTS
          currentAction.ttsParam = {
            text: ((ttsText = params == null ? undefined : params.ttsParam) == null ? undefined : ttsText.text) ?? '',
            role: ((ttsRole = params == null ? undefined : params.ttsParam) == null ? undefined : ttsRole.role) ?? 'male',
            language: ((ttsLanguage = params == null ? undefined : params.ttsParam) == null ? undefined : ttsLanguage.language) ?? 'CN',
            volume: ((ttsVolume = params == null ? undefined : params.ttsParam) == null ? undefined : ttsVolume.volume) ?? 100,
            speed: ((ttsSpeed = params == null ? undefined : params.ttsParam) == null ? undefined : ttsSpeed.speed) ?? 50
          }
        }
        break
    }
    const customAction = buildCustomAction(row)
    if (customAction) {
      processedActions.push(customAction)
      return
    }
    if (currentAction) {
      currentAction.uuid = uuidv4()
      currentAction.type = row.actionActuatorFunc
      setCustomActionType(currentAction, row)
      if (row.actionActuatorFunc === WaylineActionType.GIMBAL_ROTATE) {
        if (params.gimbalYawRotateEnable * 1) {
          currentAction.type = WaylineActionType.GIMBAL_ROTATE_YAW
        } else {
          if (params.gimbalPitchRotateEnable * 1) {
            currentAction.type = WaylineActionType.GIMBAL_ROTATE_PITCH
          }
        }
      } else if (row.actionActuatorFunc === WaylineActionType.SEARCH_LIGHT && params.searchlightOperateType === 0) {
        currentAction.type = WaylineActionType.SEARCH_LIGHT_STOP
      } else if (row.actionActuatorFunc === WaylineActionType.MEGAPHONE && params.megaphoneOperateType === 1) {
        currentAction.type = WaylineActionType.MEGAPHONE_STOP
      }
      processedActions.push(currentAction)
    }
  })
  processedActions = mergeArRecordActions(processedActions)
  return processedActions
}

export function setCustomActionType (targetAction, sourceAction) {
  switch (sourceAction.customActionType) {
    case DEVICE_TYPE.START_AR_RECORD:
      targetAction.customActionType = sourceAction.customActionType
      break
    case DEVICE_TYPE.STOP_AR_RECORD:
      targetAction.customActionType = sourceAction.customActionType
      break
    case '':
      targetAction.customActionType = ''
      break
  }
}

export function actionGroupSet (actionGroup) {
  let regularActions = []
  const multipleActions = []
  actionGroup.forEach(item => {
    if (item.actionTrigger.actionTriggerType === 'multipleTiming' || item.actionTrigger.actionTriggerType === 'multipleDistance') {
      multipleActions.push({
        ...item
      })
    } else {
      if (!Array.isArray(item.action)) {
        item.action = [item.action]
      }
      const actions = actionSet(item.action)
      regularActions = [...regularActions, ...actions]
    }
  })
  return {
    regularActionData: regularActions,
    multipleActionData: multipleActions
  }
}

export function pointFromDegrees (position, viewer) {
  const cartographic = Cesium.Cartographic.fromCartesian(position)
  const longitude = Cesium.Math.toDegrees(cartographic.longitude)
  const latitude = Cesium.Math.toDegrees(cartographic.latitude)
  const height = viewer.scene.globe.getHeight(Cesium.Cartographic.fromDegrees(longitude, latitude, 0))
  return Cesium.Cartesian3.fromDegrees(longitude, latitude, height)
}

export function setSaveObj (viewer, position, startPoint, heightMode) {
  const startCartographic = Cesium.Cartographic.fromCartesian(startPoint)
  const positionCartographic = Cesium.Cartographic.fromCartesian(position)
  const longitude = Cesium.Math.toDegrees(positionCartographic.longitude)
  const latitude = Cesium.Math.toDegrees(positionCartographic.latitude)
  const height = positionCartographic.height
  const geoidOffset = meanSeaLevel(latitude, longitude)
  const point = {
    lng: longitude,
    lat: latitude,
    h: height,
    ellipsoidHeight: 0,
    height: 0,
    executeHeight: 0
  }
  if (heightMode === WaylineMapType.EGM96) {
    point.ellipsoidHeight = height + geoidOffset
    point.height = height
    point.executeHeight = height + geoidOffset
  } else if (heightMode === WaylineMapType.RELATIVE_TO_START_POINT) {
    point.height = point.executeHeight = point.ellipsoidHeight = height - startCartographic.height
  } else if (heightMode === WaylineMapType.ABOVE_GROUND_LEVEL) {
    const groundPosition = pointFromDegrees(position, viewer)
    const groundCartographic = Cesium.Cartographic.fromCartesian(groundPosition)
    point.ellipsoidHeight = height + geoidOffset
    point.height = height - groundCartographic.height
    point.executeHeight = height + geoidOffset
  }
  return point
}

function isCustomActionType (type) {
  return !![DEVICE_TYPE.START_AR_RECORD, DEVICE_TYPE.STOP_AR_RECORD, DEVICE_TYPE.START_AI, DEVICE_TYPE.STOP_AI, DEVICE_TYPE.START_MP20_LIGHTFLASH, DEVICE_TYPE.STOP_MP20_LIGHTFLASH, DEVICE_TYPE.START_SPEAKER_PLAY, DEVICE_TYPE.STOP_SPEAKER_PLAY].includes(type)
}

function getAlgorithmParams (actions, actionUuid, actionType) {
  const algorithmParams = []
  const actionIndex = actions.findIndex(action => action.uuid === actionUuid)
  switch (actionType) {
    case DEVICE_TYPE.START_AI:
      findParams(actionIndex)
      break
    case DEVICE_TYPE.STOP_AI:
      {
        const prevIndex = actionIndex - 1
        findParams(prevIndex)
      }
      break
  }
  function findParams (startIndex) {
    for (let i = startIndex; i >= 0; i--) {
      const action = actions[i]
      if (action.type === DEVICE_TYPE.START_AI) {
        algorithmParams.push(action.customActionParam.algorithmParam)
      } else if (action.type === DEVICE_TYPE.STOP_AI) {
        break
      }
    }
  }
  if (algorithmParams.length !== 0 && !store.state.aiAlgorithmConfigUpperLimit) {
    const uniqueParams = []
    algorithmParams.forEach(param => {
      uniqueParams.includes(param) || uniqueParams.push(param)
    })
    uniqueParams.length >= 4 && !store.state.aiAlgorithmConfigUpperLimit && (store.state.aiAlgorithmConfigUpperLimit = !0)
  }
  return algorithmParams
}

function buildWpmlActions (actions, actionType, startId, droneType) {
  const wpmlActions = []
  let actionId = startId
  actions.forEach(action => {
    const wpmlAction = {
      wpml_actionId: actionId,
      wpml_customActionType: actionType,
      wpml_actionActuatorFunc: action.type,
      wpml_actionActuatorFuncParam: {}
    }
    actionId = actionId + 1
    switch (action.type) {
      case WaylineActionType.START_RECORD:
        {
          let lensType
          droneType === DRONE_WAYLINE_TYPE.M30 ? lensType = 'wide' : droneType === DRONE_WAYLINE_TYPE.Mavic3EnterpriseAdvanced ? lensType = '' : droneType === DRONE_WAYLINE_TYPE.M3D && (lensType = 'visable')
          wpmlAction.wpml_actionActuatorFuncParam = {
            wpml_payloadPositionIndex: 0,
            wpml_useGlobalPayloadLensIndex: action.useGlobalPayloadLensIndex,
            wpml_payloadLensIndex: lensType
          }
          if (action.fileSuffix) {
            wpmlAction.wpml_actionActuatorFuncParam.wpml_fileSuffix = action.fileSuffix
          }
        }
        break
      case WaylineActionType.GIMBAL_ROTATE_PITCH:
        wpmlAction.wpml_actionActuatorFuncParam = {
          wpml_gimbalHeadingYawBase: 'north',
          wpml_gimbalRotateMode: 'absoluteAngle',
          wpml_gimbalPitchRotateEnable: 1,
          wpml_gimbalPitchRotateAngle: action.gimbalPitchRotateAngle,
          wpml_gimbalRollRotateEnable: 0,
          wpml_gimbalRollRotateAngle: 0,
          wpml_gimbalYawRotateEnable: 0,
          wpml_gimbalYawRotateAngle: 0,
          wpml_gimbalRotateTimeEnable: 0,
          wpml_gimbalRotateTime: 0,
          wpml_payloadPositionIndex: 0
        }
        wpmlAction.wpml_actionActuatorFunc = WaylineActionType.GIMBAL_ROTATE
        break
    }
    wpmlActions.push(wpmlAction)
  })
  return wpmlActions
}

function buildCustomWpmlActions (actionId, action, allActions, droneType) {
  let wpmlActions = []
  switch (action.type) {
    case DEVICE_TYPE.START_AR_RECORD:
      wpmlActions = buildWpmlActions(action.actionList, action.type, actionId, droneType)
      break
    case DEVICE_TYPE.STOP_AR_RECORD:
      wpmlActions = [{
        wpml_actionId: actionId,
        wpml_customActionType: DEVICE_TYPE.STOP_AR_RECORD,
        wpml_actionActuatorFunc: WaylineActionType.STOP_RECORD,
        wpml_actionActuatorFuncParam: {
          wpml_payloadPositionIndex: locationPosition.MAIN
        }
      }]
      break
    case DEVICE_TYPE.START_AI:
      {
        const algorithmParams = getAlgorithmParams(allActions, action.uuid, action.type)
        wpmlActions = [{
          wpml_actionId: actionId,
          wpml_customActionType: DEVICE_TYPE.START_AI,
          wpml_customActionParam: {
            wpml_warnType: action.customActionParam.warnType,
            wpml_algorithmParam: action.customActionParam.algorithmParam,
            wpml_algorithmParamConfig: algorithmParams.toString()
          }
        }]
      }
      break
    case DEVICE_TYPE.STOP_AI:
      {
        const algorithmParams = getAlgorithmParams(allActions, action.uuid, action.type)
        wpmlActions = [{
          wpml_actionId: actionId,
          wpml_customActionType: DEVICE_TYPE.STOP_AI,
          wpml_customActionParam: {
            wpml_warnType: action.customActionParam.warnType,
            wpml_algorithmParam: action.customActionParam.algorithmParam,
            wpml_algorithmParamConfig: algorithmParams.toString()
          }
        }]
      }
      break
    case DEVICE_TYPE.START_MP20_LIGHTFLASH:
      wpmlActions = [{
        wpml_actionId: actionId,
        wpml_customActionType: DEVICE_TYPE.START_MP20_LIGHTFLASH,
        wpml_customActionParam: {
          wpml_psdkValue: action.customActionParam.psdkValue,
          wpml_psdkName: action.customActionParam.psdkName,
          wpml_psdkIndex: action.customActionParam.psdkIndex
        }
      }]
      break
    case DEVICE_TYPE.STOP_MP20_LIGHTFLASH:
      wpmlActions = [{
        wpml_actionId: actionId,
        wpml_customActionType: DEVICE_TYPE.STOP_MP20_LIGHTFLASH,
        wpml_customActionParam: {
          wpml_psdkValue: action.customActionParam.psdkValue,
          wpml_psdkName: action.customActionParam.psdkName,
          wpml_psdkIndex: action.customActionParam.psdkIndex
        }
      }]
      break
    case DEVICE_TYPE.START_SPEAKER_PLAY:
      wpmlActions = [{
        wpml_actionId: actionId,
        wpml_customActionType: DEVICE_TYPE.START_SPEAKER_PLAY,
        wpml_customActionParam: {
          wpml_psdkName: action.customActionParam.psdkName,
          wpml_psdkIndex: action.customActionParam.psdkIndex,
          wpml_speakerPlayType: action.customActionParam.speakerPlayType,
          wpml_speakerPlayContentId: action.customActionParam.speakerPlayContentId,
          wpml_speakerPlayLanguage: action.customActionParam.speakerPlayLanguage,
          wpml_speakerPlayVolume: action.customActionParam.speakerPlayVolume,
          wpml_speakerPlayMode: action.customActionParam.speakerPlayMode,
          wpml_speakerPlayEndWaypoint: action.customActionParam.speakerPlayEndWaypoint
        }
      }]
      break
  }
  return wpmlActions
}

function buildActionGroups (eventList, pointIndex, allActions, droneType) {
  const actionGroups = []
  const regularEvents = []
  const multipleEvents = []
  eventList.forEach(event => {
    event.type === 'multipleTiming' || event.type === 'multipleDistance' ? multipleEvents.push(event) : event.type !== 'multipleEnd' && regularEvents.push(event)
  })
  if (regularEvents.length) {
    const actionGroup = {
      wpml_actionGroupId: actionGroups.length,
      wpml_actionGroupStartIndex: pointIndex,
      wpml_actionGroupEndIndex: pointIndex,
      wpml_actionGroupMode: SEQUENCE.SEQUENCE,
      wpml_actionTrigger: {
        wpml_actionTriggerType: PointEnum.REACH_POINT
      },
      wpml_action: []
    }
    let actionId = 0
    regularEvents.forEach(event => {
      let ttsParam
      if (event != null && event.type && isCustomActionType(event == null ? undefined : event.type)) {
        const customActions = buildCustomWpmlActions(actionId, event, allActions, droneType)
        actionGroup.wpml_action.push(...customActions)
        actionId = actionId + customActions.length
        return
      }
      const wpmlAction = {
        wpml_actionId: actionId,
        wpml_actionActuatorFunc: event.type,
        wpml_actionActuatorFuncParam: {}
      }
      actionId = actionId + 1
      if (event.type === WaylineActionType.START_RECORD || event.type === WaylineActionType.TAKE_PHOTO) {
        wpmlAction.wpml_actionActuatorFuncParam = {
          wpml_payloadPositionIndex: 0,
          wpml_useGlobalPayloadLensIndex: event.useGlobalPayloadLensIndex
        }
        event.useGlobalPayloadLensIndex === 0 && (wpmlAction.wpml_actionActuatorFuncParam.wpml_payloadLensIndex = event.payloadLensIndex.join())
        if (event.fileSuffix) wpmlAction.wpml_actionActuatorFuncParam.wpml_fileSuffix = event.fileSuffix
      } else {
        if (event.type === WaylineActionType.STOP_RECORD) {
          wpmlAction.wpml_actionActuatorFuncParam = {
            wpml_payloadPositionIndex: 0
          }
          wpmlAction.wpml_actionActuatorFunc = WaylineActionType.STOP_RECORD
        } else if (event.type === WaylineActionType.HOVER) {
          wpmlAction.wpml_actionActuatorFuncParam = {
            wpml_hoverTime: event.hoverTime
          }
          wpmlAction.wpml_actionActuatorFunc = WaylineActionType.HOVER
        } else if (event.type === WaylineActionType.ROTATE_YAW) {
          wpmlAction.wpml_actionActuatorFuncParam = {
            wpml_aircraftHeading: event.aircraftHeading,
            wpml_aircraftPathMode: 'clockwise'
          }
          wpmlAction.wpml_actionActuatorFunc = WaylineActionType.ROTATE_YAW
        } else if (event.type === WaylineActionType.GIMBAL_ROTATE_YAW) {
          wpmlAction.wpml_actionActuatorFuncParam = {
            wpml_gimbalHeadingYawBase: 'north',
            wpml_gimbalRotateMode: 'absoluteAngle',
            wpml_gimbalPitchRotateEnable: 0,
            wpml_gimbalPitchRotateAngle: 0,
            wpml_gimbalRollRotateEnable: 0,
            wpml_gimbalRollRotateAngle: 0,
            wpml_gimbalYawRotateEnable: 1,
            wpml_gimbalYawRotateAngle: event.gimbalYawRotateAngle,
            wpml_gimbalRotateTimeEnable: 0,
            wpml_gimbalRotateTime: 0,
            wpml_payloadPositionIndex: 0
          }
          wpmlAction.wpml_actionActuatorFunc = WaylineActionType.GIMBAL_ROTATE
        } else if (event.type === WaylineActionType.GIMBAL_ROTATE_PITCH) {
          wpmlAction.wpml_actionActuatorFuncParam = {
            wpml_gimbalHeadingYawBase: 'north',
            wpml_gimbalRotateMode: 'absoluteAngle',
            wpml_gimbalPitchRotateEnable: 1,
            wpml_gimbalPitchRotateAngle: event.gimbalPitchRotateAngle,
            wpml_gimbalRollRotateEnable: 0,
            wpml_gimbalRollRotateAngle: 0,
            wpml_gimbalYawRotateEnable: 0,
            wpml_gimbalYawRotateAngle: 0,
            wpml_gimbalRotateTimeEnable: 0,
            wpml_gimbalRotateTime: 0,
            wpml_payloadPositionIndex: 0
          }
          wpmlAction.wpml_actionActuatorFunc = WaylineActionType.GIMBAL_ROTATE
        } else if (event.type === WaylineActionType.ZOOM) {
          wpmlAction.wpml_actionActuatorFuncParam = {
            wpml_focalLength: event.focalLength * 24,
            wpml_isUseFocalFactor: 0,
            wpml_payloadPositionIndex: 0
          }
          wpmlAction.wpml_actionActuatorFunc = WaylineActionType.ZOOM
        } else if (event.type === WaylineActionType.CUSTOM_DIRNAME) {
          wpmlAction.wpml_actionActuatorFuncParam = {
            wpml_payloadPositionIndex: 0
          }
          if (event.directoryName) {
            wpmlAction.wpml_actionActuatorFuncParam.directoryName = event.directoryName
          }
          wpmlAction.wpml_actionActuatorFunc = WaylineActionType.CUSTOM_DIRNAME
        } else if (event.type === WaylineActionType.PANOSHOT) {
          wpmlAction.wpml_actionActuatorFuncParam = {
            wpml_payloadPositionIndex: 0,
            wpml_useGlobalPayloadLensIndex: 0,
            wpml_panoShotSubMode: 'panoShot_360'
          }
          wpmlAction.wpml_actionActuatorFunc = WaylineActionType.PANOSHOT
        } else if (event.type === WaylineActionType.SEARCH_LIGHT) {
          wpmlAction.wpml_actionActuatorFuncParam = {
            wpml_payloadPositionIndex: 0,
            wpml_searchlightOperateType: event.searchlightOperateType,
            wpml_searchlightBrightness: event.searchlightBrightness
          }
          wpmlAction.wpml_actionActuatorFunc = WaylineActionType.SEARCH_LIGHT
        } else if (event.type === WaylineActionType.SEARCH_LIGHT_STOP) {
          wpmlAction.wpml_actionActuatorFuncParam = {
            wpml_payloadPositionIndex: 0,
            wpml_searchlightOperateType: 0,
            wpml_searchlightBrightness: 0
          }
          wpmlAction.wpml_actionActuatorFunc = WaylineActionType.SEARCH_LIGHT_STOP
        } else if (event.type === WaylineActionType.MEGAPHONE) {
          wpmlAction.wpml_actionActuatorFuncParam = {
            wpml_payloadPositionIndex: 0,
            wpml_actionUUID: event.actionUUID === '' ? event.uuid : event.actionUUID,
            wpml_megaphoneOperateType: event.megaphoneOperateType,
            wpml_megaphoneOperateVolume: event.megaphoneOperateVolume,
            wpml_megaphoneOperateLoop: event.megaphoneOperateLoop,
            wpml_megaphoneFileOriginalName: event.megaphoneFileOriginalName,
            wpml_megaphoneFileBitrate: event.megaphoneFileBitrate,
            wpml_megaphoneOperateFilePath: event.megaphoneOperateFilePath,
            wpml_megaphoneFileName: event.megaphoneFileName,
            wpml_megaphoneFileMd5: event.megaphoneFileMd5
          }
          if (event.megaphoneFileName === '') {
            wpmlAction.wpml_actionActuatorFuncParam.ttsParam = {
              text: (ttsParam = event.ttsParam) == null ? undefined : ttsParam.text,
              role: event.ttsParam.role,
              language: event.ttsParam.language,
              volume: event.ttsParam.volume,
              speed: event.ttsParam.speed
            }
            wpmlAction.wpml_actionActuatorFuncParam.wpml_megaphoneFileOriginalName = 'tts'
          }
          wpmlAction.wpml_actionActuatorFunc = WaylineActionType.MEGAPHONE
        } else if (event.type === WaylineActionType.MEGAPHONE_STOP) {
          wpmlAction.wpml_actionActuatorFuncParam = {
            wpml_payloadPositionIndex: 0,
            wpml_megaphoneOperateType: 1,
            wpml_megaphoneOperateVolume: 0,
            wpml_megaphoneOperateLoop: 1,
            wpml_megaphoneFileBitrate: 65535
          }
          wpmlAction.wpml_actionActuatorFunc = WaylineActionType.MEGAPHONE_STOP
        }
      }
      actionGroup.wpml_action.push(wpmlAction)
    })
    actionGroups.push(actionGroup)
  }
  if (multipleEvents.length) {
    multipleEvents.forEach(event => {
      const multipleActionConfig = getMultipleActionConfig(event, allActions)
      if (!multipleActionConfig.isAdd) { return }
      const actionGroup = {
        wpml_actionGroupId: actionGroups.length,
        wpml_actionGroupStartIndex: event.parentIndex,
        wpml_actionGroupEndIndex: multipleActionConfig.actionGroupEndIndex,
        wpml_actionGroupMode: SEQUENCE.SEQUENCE,
        wpml_actionTrigger: {
          wpml_actionTriggerType: event.type,
          wpml_actionTriggerParam: event.actionTriggerParam
        },
        wpml_action: [{
          wpml_actionId: 0,
          wpml_actionActuatorFunc: WaylineActionType.TAKE_PHOTO,
          wpml_actionActuatorFuncParam: {}
        }]
      }
      const actionParam = {
        wpml_payloadPositionIndex: locationPosition.MAIN,
        wpml_useGlobalPayloadLensIndex: event.useGlobalPayloadLensIndex
      }
      if (event.useGlobalPayloadLensIndex === 0) {
        actionParam.wpml_payloadLensIndex = event.payloadLensIndex.join()
      }
      actionGroup.wpml_action[0].wpml_actionActuatorFuncParam = actionParam
      actionGroups.push(actionGroup)
    })
  }
  return actionGroups
}

function getMultipleActionConfig (multipleAction, allActions) {
  let prevActions = []
  let nextActions = []
  allActions.forEach((action, index) => {
    if (action.type === multipleAction.type && action.uuid === multipleAction.uuid) {
      prevActions = allActions.slice(action, index)
      nextActions = allActions.slice(index, allActions.length)
    }
  })
  let canAdd = !0
  prevActions.forEach(action => {
    if (action.type === 'multipleTiming' || action.type === 'multipleDistance') {
      canAdd = !1
      if (action.type === 'multipleEnd') canAdd = !0
    }
  })
  let endIndex = null
  if (canAdd) {
    nextActions.forEach(action => {
      if (action.type === 'multipleEnd' && endIndex === null) endIndex = action.parentIndex
    })
    if (endIndex === null) {
      (endIndex = multipleAction.parentIndex)
    }
  }
  return {
    isAdd: canAdd,
    actionGroupEndIndex: endIndex
  }
}

export function getKmlObj (pointList, config, allActions, droneType) {
  const kmlPoints = []
  pointList.forEach((point, index) => {
    const actionGroups = buildActionGroups(point.eventList, index, allActions, droneType)
    let turnMode = config.waypointTurnMode
    let dampingDist = 0.2
    let useStraightLine = 1
    if (!point.useGlobalTurnParam) {
      turnMode = point.waypointTurnMode || config.waypointTurnMode
      if (turnMode === WaylineSmoothType.COORDINATE_TURN) {
        dampingDist = point?.waypointTurnDampingDist || 0
      }
    }

    if (turnMode === WaylineSmoothType.SMOOTH_OVER_POINTS) {
      useStraightLine = 1
      turnMode = WaylineSmoothType.TO_POINT_AND_PASS_WITH_CONTINUITY_CURVATURE
    } else if (
      turnMode === WaylineSmoothType.TO_POINT_AND_STOP_WITH_CONTINUITY_CURVATURE ||
      turnMode === WaylineSmoothType.TO_POINT_AND_PASS_WITH_CONTINUITY_CURVATURE
    ) {
      useStraightLine = 0
    }
    let useGlobalSpeed = 1
    Number(point.waypointSpeed) !== Number(config.autoFlightSpeed) && (useGlobalSpeed = 0)
    const kmlPoint = {
      Point: new GetPoint(point.lng, point.lat),
      wpml_index: point.index,
      wpml_ellipsoidHeight: point.ellipsoidHeight,
      wpml_height: point.height,
      wpml_executeHeight: point.executeHeight || config.executeHeight,
      wpml_waypointSpeed: point.waypointSpeed || config.waypointSpeed,
      wpml_waypointHeadingParam: {
        wpml_waypointHeadingMode: point?.waypointHeadingParam?.waypointHeadingMode,
        wpml_waypointHeadingAngle: point?.waypointHeadingParam?.waypointHeadingAngle,
        wpml_waypointPoiPoint: new GetNewPoint().toString(),
        wpml_waypointHeadingAngleEnable: 0,
        wpml_waypointHeadingPathMode: point?.waypointHeadingParam?.waypointHeadingPathMode,
        wpml_waypointHeadingPoiIndex: 0
      },
      wpml_waypointTurnParam: {
        wpml_waypointTurnMode: turnMode,
        wpml_waypointTurnDampingDist: dampingDist
      },
      wpml_useGlobalHeight: point.useGlobalHeight || config.useGlobalHeight || 1,
      wpml_useGlobalSpeed: useGlobalSpeed || config.useGlobalSpeed || 1,
      wpml_useGlobalHeadingParam: point.useGlobalHeadingParam,
      wpml_useGlobalTurnParam: point.useGlobalTurnParam,
      wpml_useStraightLine: useStraightLine,
      wpml_actionGroup: actionGroups,
      wpml_waypointGimbalHeadingParam: {
        wpml_waypointGimbalPitchAngle: 0,
        wpml_waypointGimbalYawAngle: 0
      },
      wpml_isRisky: BooleanEnum.FALSE,
      wpml_waypointWorkType: BooleanEnum.FALSE
    }
    kmlPoints.push(kmlPoint)
  })
  return kmlPoints
}

export function getKml (config, kmlPoints) {
  const currentTime = new Date().getTime()
  let executeHeightMode
  config.heightMode === WaylineMapType.RELATIVE_TO_START_POINT ? executeHeightMode = WaylineTypeMap.RELATIVE_TO_START_POINT : executeHeightMode = WaylineTypeMap.WGS84
  let useStraightLine = 1
  let globalTurnMode = config.waypointTurnMode
  if (config.waypointTurnMode === WaylineSmoothType.SMOOTH_OVER_POINTS) {
    globalTurnMode = WaylineSmoothType.TO_POINT_AND_PASS_WITH_CONTINUITY_CURVATURE
  } else {
    if (config.waypointTurnMode === WaylineSmoothType.TO_POINT_AND_STOP_WITH_CONTINUITY_CURVATURE || config.waypointTurnMode === WaylineSmoothType.TO_POINT_AND_PASS_WITH_CONTINUITY_CURVATURE) {
      useStraightLine = 0
    }
  }
  return {
    wpml_author: config.author,
    wpml_createTime: config.createTime,
    wpml_updateTime: currentTime,
    wpml_missionConfig: {
      wpml_flyToWaylineMode: config.flyToWaylineMode,
      wpml_finishAction: config.finishAction,
      wpml_exitOnRCLost: BtnCallBackLoading.GO_CONTINUE,
      wpml_executeRCLostAction: BtnLoading.GO_BACK,
      wpml_takeOffSecurityHeight: config.takeOffSecurityHeight,
      wpml_takeOffRefPoint: new GetNewPoint(config.startLat, config.startLng, config.startHeight).toString(),
      wpml_takeOffRefPointAGLHeight: 0,
      wpml_globalTransitionalSpeed: config.globalTransitionalSpeed,
      wpml_globalRTHHeight: config.globalRTHHeight,
      wpml_droneInfo: {
        wpml_droneEnumValue: config.droneEnumValue,
        wpml_droneSubEnumValue: config.droneSubEnumValue
      },
      wpml_payloadInfo: {
        wpml_payloadEnumValue: config.payloadEnumValue,
        wpml_payloadSubEnumValue: 0,
        wpml_payloadPositionIndex: 0
      }
    },
    Folder: [{
      wpml_templateType: TEMPLATE_TYPE[config.templateType],
      wpml_templateId: 0,
      wpml_waylineCoordinateSysParam: {
        wpml_coordinateMode: WaylineTypeMap.WGS84,
        wpml_heightMode: config.heightMode
      },
      wpml_autoFlightSpeed: config.autoFlightSpeed,
      wpml_globalHeight: config.globalHeight,
      wpml_caliFlightEnable: BooleanEnum.FALSE,
      wpml_gimbalPitchMode: config.gimbalPitchMode,
      wpml_globalWaypointHeadingParam: {
        wpml_waypointHeadingMode: config.waypointHeadingMode,
        wpml_waypointHeadingAngle: 0,
        wpml_waypointPoiPoint: new GetNewPoint().toString(),
        wpml_waypointHeadingPathMode: FollowBadType.FOLLOW_BAD_ARC,
        wpml_waypointHeadingPoiIndex: 0
      },
      wpml_globalUseStraightLine: useStraightLine,
      wpml_globalWaypointTurnMode: globalTurnMode,
      Placemark: kmlPoints,
      wpml_waylineId: 0,
      wpml_executeHeightMode: executeHeightMode,
      wpml_distance: config.distance,
      wpml_duration: config.duration,
      wpml_payloadParam: buildPayloadParam(config)
    }]
  }
}

function buildPayloadParam (config) {
  let payloadParam
  config.droneEnumValue !== DRONE_WAYLINE_TYPE.Mavic3EnterpriseAdvanced
    ? payloadParam = {
      wpml_payloadPositionIndex: locationPosition.MAIN,
      wpml_imageFormat: config.imageFormat.join()
    }
    : payloadParam = {
      wpml_payloadPositionIndex: locationPosition.MAIN
    }
  return payloadParam
}

export const handleSetAct = (action, pointList, aiList, aiEventList, aiIndex, cb?: Function) => {
  if (disabledAction.includes(action)) return
  const actionObj: any = {
    type: action,
    uuid: uuidv4()
  }
  const droneType = store.state.editInfo.drone_type
  const type = PAYLOAD_TRY_TYPE[PAYLOAD_TRY_TYPE[droneType]]
  if (action === WaylineActionType.START_RECORD || action === 'multipleTiming' || action === 'multipleDistance' || action === WaylineActionType.TAKE_PHOTO) {
    actionObj.useGlobalPayloadLensIndex = 1
    actionObj.payloadLensIndex = []
    if (droneType === type) {
      actionObj.useGlobalPayloadLensIndex = 0
      actionObj.payloadLensIndex = []
    }
  }
  switch (action) {
    case WaylineActionType.START_RECORD:
      actionObj.fileSuffix = ''
      break
    case 'multipleTiming':
      actionObj.actionTriggerParam = 3
      break
    case 'multipleDistance':
      actionObj.actionTriggerParam = 10
      break
    case WaylineActionType.HOVER:
      actionObj.hoverTime = 10
      break
    case WaylineActionType.ROTATE_YAW:
      actionObj.aircraftHeading = Number((state.drawRouteTool?._frustumTool._heading - 90).toFixed(1))
      globalType.value = Number((state.drawRouteTool?._frustumTool?._heading - 90).toFixed(1))
      headTop.value = 0
      break
    case WaylineActionType.GIMBAL_ROTATE_YAW:
      actionObj.gimbalYawRotateAngle = Number((state.drawRouteTool?._frustumTool._heading - 90).toFixed(1))
      globalType.value = Number((state.drawRouteTool?._frustumTool?._heading - 90).toFixed(1))
      headTop.value = 0
      break
    case WaylineActionType.GIMBAL_ROTATE_PITCH:
      actionObj.gimbalPitchRotateAngle = Number(((state.drawRouteTool?._frustumTool._pitch - 90) * -1).toFixed(1))
      globalType.value = state.drawRouteTool?._frustumTool._heading - 90
      headTop.value = Number(((state.drawRouteTool?._frustumTool._pitch - 90) * -1).toFixed(1))
      break
    case WaylineActionType.TAKE_PHOTO:
      actionObj.fileSuffix = ''
      break
    case WaylineActionType.ZOOM:
      actionObj.focalLength = 2
      break
    case WaylineActionType.CUSTOM_DIRNAME:
      actionObj.directoryName = ''
      break
    case WaylineActionType.SEARCH_LIGHT:
      actionObj.searchlightOperateType = 1
      actionObj.searchlightBrightness = 100
      break
    case WaylineActionType.SEARCH_LIGHT_STOP:
      actionObj.searchlightOperateType = 0
      actionObj.searchlightBrightness = 0
      break
    case WaylineActionType.MEGAPHONE:
      actionObj.speakerPlayType = waylineFont.TTS
      actionObj.megaphoneOperateType = 0
      actionObj.actionUUID = ''
      actionObj.megaphoneOperateVolume = 100
      actionObj.megaphoneOperateLoop = 0
      actionObj.megaphoneFileOriginalName = 'tts'
      actionObj.megaphoneFileBitrate = 4
      actionObj.megaphoneOperateFilePath = ''
      actionObj.megaphoneFileName = ''
      actionObj.megaphoneFileMd5 = ''
      actionObj.ttsParam = {
        text: '喊话示例',
        role: 'male',
        language: 'CN',
        volume: 100,
        speed: 50
      }
      break
    case WaylineActionType.MEGAPHONE_STOP:
      actionObj.megaphoneOperateType = 1
      actionObj.megaphoneOperateVolume = 0
      actionObj.megaphoneOperateLoop = 1
      actionObj.megaphoneFileBitrate = 65535
      break
    case WaylineActionType.START_AI:
      actionObj.customActionParam = {
        algorithmParam: `${aiList[aiIndex].id}-${aiList[aiIndex].id}-${aiEventList[0].id}`,
        algorithmParamConfig: [],
        aiType: aiList && aiList.length > 0 && aiList[aiIndex] ? aiList[aiIndex].id : '',
        aiEvent: aiEventList && aiEventList.length > 0 ? aiEventList[0].id : '',
        warnType: '0'
      }
      break
    case WaylineActionType.STOP_AI:
      actionObj.customActionParam = {
        algorithmParam: '',
        algorithmParamConfig: [],
        warnType: '0'
      }
      break
  }
  setPush(store.state.currentRoutePosIndex, addActionIndex.value, actionObj, pointList)
  pointList[store.state.currentRoutePosIndex].eventList.push(actionObj)
  activeBtnIndex.value = `${store.state.currentRoutePosIndex}-${addActionIndex.value}`
  addActionIndex.value++
  nextTick(() => {
    if (cb) {
      cb()
    }
  })
}

function setPoint (action, pointIndex, pointList) {
  if (cellAction.type === DEVICE_TYPE.START_AR_RECORD) {
    const fileSuffix = action.actionList[1].fileSuffix
    showTip.data.inputName = fileSuffix
    fileSuffix ? showTip.data.text = fileSuffix : showTip.data.text = 'CZI_YYYYMMDDhhmm_XXX_'
    globalType.value = state.drawRouteTool?._frustumTool._heading - 90
    headTop.value = -90
    setFrustumUpdate(pointList[pointIndex].position, globalType.value, (headTop.value - 90) * -1, pointList[pointIndex])
    artSetE(pointList)
  }
}

export function setPush (pointIndex, actionIndex, action, pointList) {
  cellAction.type = action.type
  cellAction.actionParams = action
  cellAction.selectIndex = actionIndex
  cellAction.actionEventTitle = `${pointIndex + 1} / ${cellAction.selectIndex + 1}`

  if (cellAction.type === WaylineActionType.START_RECORD ||
    cellAction.type === WaylineActionType.TAKE_PHOTO) {
    showTip.data.inputName = action.fileSuffix
    if (action.fileSuffix) {
      showTip.data.text = action.fileSuffix
    } else {
      showTip.data.text = 'CZI_YYYYMMDDhhmm_XXX_'
    }
  } else if (cellAction.type === WaylineActionType.CUSTOM_DIRNAME) {
    showTip.data.inputName = action.directoryName
    if (action.directoryName) {
      showTip.data.text = action.directoryName
    } else {
      showTip.data.text = 'CZI_YYYYMMDDhhmm_XXX_'
    }
  } else if (cellAction.type === WaylineActionType.ROTATE_YAW ||
    cellAction.type === WaylineActionType.GIMBAL_ROTATE_YAW) {
    if (cellAction.type === WaylineActionType.ROTATE_YAW) {
      globalType.value = cellAction.actionParams.aircraftHeading
    } else {
      globalType.value = cellAction.actionParams.gimbalYawRotateAngle
    }
    headTop.value = 0
    setFrustumUpdate(pointList[pointIndex].position, globalType.value, (headTop.value - 90) * -1, pointList[pointIndex])
    artSetE(pointList)
  } else if (cellAction.type === WaylineActionType.GIMBAL_ROTATE_PITCH) {
    globalType.value = state.drawRouteTool?._frustumTool._heading - 90
    headTop.value = cellAction.actionParams.gimbalPitchRotateAngle
    setFrustumUpdate(pointList[pointIndex].position, globalType.value, (headTop.value - 90) * -1, pointList[pointIndex])
    artSetE(pointList)
  }
  setPoint(action, pointIndex, pointList)
}

export function artSetE (pointList) {
  const point = pointList[store.state.currentRoutePosIndex]
  if (cellAction.type === WaylineActionType.ROTATE_YAW || cellAction.type === WaylineActionType.GIMBAL_ROTATE_YAW) {
    if (cellAction.type === WaylineActionType.ROTATE_YAW) {
      globalType.value = cellAction.actionParams.aircraftHeading * 1
    } else {
      globalType.value = cellAction.actionParams.gimbalYawRotateAngle * 1
    }
    const frustum = state.drawRouteTool?._frustumTool
    state.drawRouteTool.changeFrustum(point.entityId, globalType.value, (headTop.value - 90) * -1)
    showFrustum(point)
    frustum.update(pointList[store.state.currentRoutePosIndex].position, globalType.value, (headTop.value - 90) * -1)
  } else if (cellAction.type === WaylineActionType.GIMBAL_ROTATE_PITCH) {
    headTop.value = cellAction.actionParams.gimbalPitchRotateAngle
    const frustum = state.drawRouteTool?._frustumTool
    state.drawRouteTool.changeFrustum(point.entityId, globalType.value, (headTop.value - 90) * -1)
    showFrustum(point)
    frustum.update(pointList[store.state.currentRoutePosIndex].position, globalType.value, (headTop.value - 90) * -1)
  }
}

/** 更新视锥体位置 */
export function setFrustumUpdate (position, heading, pitch, point) {
  showFrustum(point)
  const frustum = state.drawRouteTool?.getFrustumVisualizer(point.entityId)
  frustum?.update(position, heading, pitch)
}

/** 显示视锥体 */
export function showFrustum (item) {
  // const allFrustum = state.drawRouteTool?.getAllFrustums()
  // if (allFrustum) {
  //   state.drawRouteTool.hideCurrentFrustum()
  //   allFrustum.forEach((frustum) => {
  //     frustum.hide()
  //   })
  // }
  // state.drawRouteTool?.showCurrentFrustum()
  // setTimeout(() => {
  //   state.drawRouteTool.setFrustum(item.entityId)
  // }, 100)
  if (!item) return

  const frustum = state.drawRouteTool?._frustumTool
  if (frustum) {
    frustum.update(item.position, item.hpr.heading - 90, item.hpr.pitch)
    frustum.show()
  } else {
    const entity = state.drawRouteTool?.getEntity(item.entityId)
    const cartesianPoint = Cesium.Cartographic.fromCartesian(entity.position)
    const longitude = Cesium.Math.toDegrees(cartesianPoint.longitude)
    const latitude = Cesium.Math.toDegrees(cartesianPoint.latitude)
    const height = getHeight()
    const waypoint = { longitude: longitude, latitude: latitude, height }
    const obj = {
      entity: entity,
      ...item
    }
    state.drawRouteTool?.addFrustum(waypoint, obj.entity)
  }
}

/**
* 设置相机视图
*
* @param viewer Cesium 视图对象
* @param heading 相机朝向，单位为度
* @param pitch 相机俯仰角，单位为度
* @param targetPosition 目标位置，Cesium.Cartesian3 类型
*/
export function setCameraView (viewer: Cesium.Viewer, heading: number, pitch: number, targetPosition: Cesium.Cartesian3) {
  // 处理俯仰角边界情况：避免完全向下的视角
  let adjustedPitch = pitch
  if (pitch === -90) {
    adjustedPitch = pitch + 0.01 // 轻微偏移避免极端角度
  }

  // 设置相机看向目标位置，并指定朝向
  viewer.camera.lookAt(targetPosition, new Cesium.HeadingPitchRange(
    Cesium.Math.toRadians(heading), // 转换为弧度
    Cesium.Math.toRadians(adjustedPitch), // 转换为弧度
    1 // 与目标的距离
  ))

  // 计算相机视场角 (FOV)
  // 基于传感器尺寸计算：4.8mm / (2 * 4.5mm) 的反正切值
  const sensorRatio = 4.8 / (2 * 4.5) // 约 0.533
  const fovRadians = 2 * Math.atan(sensorRatio)
  const fovDegrees = fovRadians * (180 / Math.PI)
  const finalFov = 1 / (1 / fovDegrees) // 实际上就是 fovDegrees

  // 设置相机视场角
  viewer.camera.frustum.fov = Cesium.Math.toRadians(finalFov)
}
/**
 * 生成圆形/环形地理多边形坐标
 * @param {Object|Array} center - 中心点坐标或地理要素
 * @param {number} radius - 半径（单位可能为米或度）
 * @param {Object} options - 配置选项
 * @param {number} options.steps - 分段数量，默认64
 * @param {Object} options.properties - 地理要素属性
 * @returns {Object} GeoJSON Polygon 要素
 */
export function generateCircularPolygon (center, radius, options = {}) {
  const steps = options.steps || 64

  // 提取属性数据
  const properties = options.properties ||
    (!Array.isArray(center) &&
      center.type === 'Feature' &&
      center.properties)
    ? center.properties
    : {}

  const coordinates = []

  // 生成圆形坐标点
  for (let i = 0; i < steps; i++) {
    const angle = i * -360 / steps // 逆时针生成点
    const point = calculatePointOnCircle(center, radius, angle, options)
    coordinates.push(point.geometry.coordinates)
  }

  // 闭合多边形（首尾相连）
  coordinates.push(coordinates[0])

  // 创建 GeoJSON Polygon 要素
  return createGeoJSONPolygon([coordinates], properties)
}
/**
 * 计算圆上某点的坐标
 * @param {Array|Object} center - 中心点
 * @param {number} radius - 半径
 * @param {number} angle - 角度（度）
 * @param {Object} options - 配置
 * @returns {Object} GeoJSON Point 要素
 */
export function calculatePointOnCircle (center, radius, angle, options) {
  const centerCoords = extractCoordinates(center)
  const radian = angle * Math.PI / 180

  // 计算新坐标（简化版，实际可能有投影转换）
  const x = centerCoords[0] + radius * Math.cos(radian)
  const y = centerCoords[1] + radius * Math.sin(radian)

  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [x, y]
    },
    properties: {}
  }
}
/**
 * 创建 GeoJSON Polygon 要素
 * @param {Array} rings - 多边形环坐标
 * @param {Object} properties - 要素属性
 * @returns {Object} GeoJSON Polygon 要素
 */
function createGeoJSONPolygon (rings, properties) {
  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: rings
    },
    properties: properties
  }
}
/**
 * 从各种格式中提取坐标
 */
function extractCoordinates (center) {
  if (Array.isArray(center)) {
    return center // 直接是坐标数组 [lng, lat]
  }
  if (center.type === 'Feature' && center.geometry) {
    return center.geometry.coordinates
  }
  if (center.coordinates) {
    return center.coordinates
  }
  throw new Error('无法提取坐标')
}

export function setViewScene (viewer) {
  viewer.scene.screenSpaceCameraController.enableZoom = true
  viewer.scene.screenSpaceCameraController.enableRotate = true
  viewer.scene.screenSpaceCameraController.enableTranslate = true
  viewer.scene.screenSpaceCameraController.enableLook = true
  viewer.scene.screenSpaceCameraController.enableTilt = true
}

export function setCameraScene (e) {
  e.scene.screenSpaceCameraController.enableZoom = false
  e.scene.screenSpaceCameraController.enableRotate = false
  e.scene.screenSpaceCameraController.enableTranslate = false
  e.scene.screenSpaceCameraController.enableLook = false
  e.scene.screenSpaceCameraController.enableTilt = false
}

const Ie = ref()
const circleName = ref('')
const circleObj = reactive({
  centerLng: null,
  centerLat: null,
  radius: null,
  area: null,
  perimeter: null,
  color: null,
  creator: null
})
export function setCircleObj (obj) {
  circleObj.area = obj.area
  circleObj.perimeter = obj.perimeter
  circleObj.centerLng = obj.center[0]
  circleObj.centerLat = obj.center[1]
  circleObj.radius = obj.radius
  circleObj.color = obj.color
}
export function getGeoJson (obj, flag) {
  const geojson = {
    content: {
      type: 'Feature',
      geometry: {
        coordinates: obj.center,
        radius: obj.radius,
        type: 'Circle'
      },
      properties: {
        clampToGround: true,
        color: obj.color
      }
    }
  }
  flag ? geojson.name = circleName.value : geojson.type = 3
  return geojson
}
export function setWaypointTurnMode (routePositions) {
  routePositions.forEach(row => {
    let waypointTurnMode = row.waypointTurnMode
    if (row.useGlobalTurnParam * 1 === 1) {
      waypointTurnMode = currentSettingData.waypointTurnMode
    }
    if (CoordinatesType.includes(waypointTurnMode)) {
      row.eventList = row.eventList.filter(item => item.type !== WaylineActionType.HOVER && item.type !== WaylineActionType.PANOSHOT)
    }
  }
  )
}

export function getPlackmarkFlag (v) {
  let flag = false
  for (let l = 0; l < v.length; l++) {
    if (!Array.isArray(v[l].actionGroup) && v[l].actionGroup) {
      [v[l].actionGroup].forEach(row => {
        if (!flag) {
          flag = row.action.some(item => item.customActionType === WaylineActionType.START_AR_RECORD || item.customActionType === WaylineActionType.STOP_AR_RECORD)
        }
      })
    } else if (Array.isArray(v[l].actionGroup)) {
      v[l].actionGroup.forEach(row => {
        if (!flag) {
          flag = row.action.some(item => item.customActionType === WaylineActionType.START_AR_RECORD || item.customActionType === WaylineActionType.STOP_AR_RECORD)
        }
      })
    }

    if (!flag) continue
  }
  return flag
}

/** 获取模拟时长 */
export function getRouterTime (list: any[], obj: any, len: number) {
  let totalTime = 0

  totalTime += obj.hoverTime || 0
  totalTime += obj.panoShotTimes || 0
  totalTime += obj.startRecordTimes || 0
  totalTime += obj.zoomTimes || 0
  totalTime += obj.psdkTimes || 0
  totalTime += obj.rotateYawTimes || 0
  totalTime += obj.gimbalRotateYawTimes || 0
  totalTime += obj.gimbalRotatePitchTimes || 0
  totalTime += obj.takePhotoTimes || 0
  totalTime += (len - 2) * 4

  list.forEach((item, index) => {
    if (index === list.length - 1) return
    const distance = Cesium.Cartesian3.distance(item.position, list[index + 1].position)
    totalTime += distance / (item.waypointSpeed || 1)
  })

  if (totalTime > 60) {
    const minutes = Math.floor(totalTime / 60)
    const seconds = Math.round(totalTime % 60)
    return {
      timeFormat: `${minutes}m${seconds}s`,
      timeSec: Math.round(totalTime)
    }
  } else {
    return {
      timeFormat: `${Math.round(totalTime)}s`,
      timeSec: Math.round(totalTime)
    }
  }
}

export const setPointEntity = (view, position, id) => {
  view.entities.add({
    id: id,
    position: position,
    point: {
      pixelSize: 20,
      color: Cesium.Color.fromCssColorString('#dcdfe6'),
      scaleByDistance: new Cesium.NearFarScalar(1e3, 1, 5e5, 0.5),
      zIndex: 2
    }
  })
}
export const lens = 0

export function setPolyline (view, row) {
  const i = view.entities.add({
    id: row.id,
    polyline: {
      positions: row.positions,
      width: 6,
      material: new PolylineDirectionMaterial()
    }
  })
  view.zoomTo(i)
}
export function getTimeSecond (actionType, actionConfig) {
  let time = 0
  switch (actionType) {
    case WaylineActionType.HOVER:
      time = actionConfig.hoverTime * 1
      break
    case WaylineActionType.PANOSHOT:
      time = 50
      break
    case WaylineActionType.TAKE_PHOTO:
      time = 1
      break
    case WaylineActionType.START_RECORD:
      time = 1
      break
    case WaylineActionType.ZOOM:
      time = 3
      break
    case WaylineActionType.ROTATE_YAW:
      time = 2
      break
    case WaylineActionType.GIMBAL_ROTATE_YAW:
      time = 2
      break
    case WaylineActionType.GIMBAL_ROTATE_PITCH:
      time = 1
      break
    case WaylineActionType.MEGAPHONE:
    case WaylineActionType.MEGAPHONE_STOP:
    case WaylineActionType.SEARCH_LIGHT:
    case WaylineActionType.SEARCH_LIGHT_STOP:
      time = 1
      break
    default:
      time = 0
      break
  }
  return time
}
export function getDistance (startPoint, endPoint) {
  return Cesium.Cartesian3.distance(startPoint, endPoint)
}
export function initializeFlightTimeline (viewer: any, flightData: any, playbackConfig: any) {
  const { startTime, stopTime, interval } = configureClock(viewer, flightData, playbackConfig.multiplier)
  const { pathPositionProperty, orientationProperty, actionProperty } = createFlightPathProperties(flightData, playbackConfig, stopTime)

  addFlightPathEntity(viewer, playbackConfig.pathId, pathPositionProperty, interval, startTime, stopTime)
  addDroneModelEntity(viewer, playbackConfig.modelId, pathPositionProperty, orientationProperty)

  return actionProperty
}

export function configureClock (viewer: any, flightData: any, speedMultiplier: number = 1) {
  const startTimeStr = flightData.startTime
  const endTimeStr = flightData.endTime
  const startJulianDate = Cesium.JulianDate.fromDate(new Date(startTimeStr))
  const endJulianDate = Cesium.JulianDate.fromDate(new Date(endTimeStr))
  const totalDuration = flightData.totalTime

  viewer.clock.startTime = startJulianDate
  viewer.clock.stopTime = endJulianDate
  viewer.clock.clockStep = Cesium.ClockStep.SYSTEM_CLOCK_MULTIPLIER
  viewer.clock.clockRange = Cesium.ClockRange.CLAMPED
  viewer.clock.shouldAnimate = false
  viewer.clock.multiplier = speedMultiplier ?? 1
  viewer.clock.currentTime = startJulianDate.clone()

  return {
    startTime: startJulianDate,
    stopTime: endJulianDate,
    interval: totalDuration
  }
}

export function createFlightPathProperties (flightData: any, cameraConfig: any, endTime: any) {
  const positionProperty = new Cesium.SampledPositionProperty()
  const orientationProperty = new Cesium.SampledProperty(Cesium.Quaternion)
  const modelHeadingProperty = new Cesium.SampledProperty(Number)
  const frustumHeadingProperty = new Cesium.SampledProperty(Number)
  const frustumPitchProperty = new Cesium.SampledProperty(Number)
  const mapZoomProperty = new Cesium.SampledProperty(Number)

  let lastPitch = 0
  let lastZoom = 0

  for (let waypointIndex = 0; waypointIndex < flightData.waypointInfo.length; waypointIndex++) {
    const waypoint_now = flightData.waypointInfo[waypointIndex]
    const totalActionTime = waypoint_now.actionTimeInterval + waypoint_now.otherTimeInterval
    const waypointTime = Cesium.JulianDate.fromDate(new Date(waypoint_now.startTime))
    const waypointPosition = calculateWaypointPosition(waypoint_now)

    positionProperty.addSample(waypointTime, waypointPosition)

    if (totalActionTime > 0) {
      const actionEndTimeMs = dayjs(waypoint_now.startTime).add(totalActionTime, 'second').valueOf()
      const actionEndJulianTime = Cesium.JulianDate.fromDate(new Date(actionEndTimeMs))
      positionProperty.addSample(actionEndJulianTime, waypointPosition)
    }

    const cameraResult = calculateCameraProperties(flightData, cameraConfig, {
      element: waypoint_now,
      curIndex: waypointIndex,
      time: waypointTime,
      position: waypointPosition,
      completedActionTimeSec: totalActionTime,
      lastPitch: lastPitch,
      lastZoom: lastZoom
    }, {
      orientationProperty: orientationProperty,
      modelHeadingProperty: modelHeadingProperty,
      frustumHeadingProperty: frustumHeadingProperty,
      frustumPitchProperty: frustumPitchProperty,
      mapZoomProperty: mapZoomProperty
    })

    lastPitch = cameraResult.pitch
    lastZoom = cameraResult.zoom
  }

  const lastWaypoint = flightData.waypointInfo[flightData.waypointInfo.length - 1]
  const lastPosition = calculateWaypointPosition(lastWaypoint)
  positionProperty.addSample(endTime, lastPosition)

  return {
    pathPositionProperty: positionProperty,
    orientationProperty: orientationProperty,
    actionProperty: {
      modelHeadingProperty: modelHeadingProperty,
      frustumHeadingProperty: frustumHeadingProperty,
      frustumPitchProperty: frustumPitchProperty,
      mapZoomProperty: mapZoomProperty
    }
  }
}

function addFlightPathEntity (viewer: any, pathId: string, positionProperty: any, duration: number, startTime: any, endTime: any) {
  viewer.entities.add({
    id: pathId,
    position: positionProperty,
    name: 'path',
    path: {
      show: true,
      leadTime: 0,
      trailTime: duration,
      width: 9,
      resolution: 1,
      material: new PolylineDirectionMaterial('#2d8cf0')
    },
    availability: new Cesium.TimeIntervalCollection([new Cesium.TimeInterval({
      start: startTime,
      stop: endTime,
      isStartIncluded: true,
      isStopIncluded: true
    })])
  })
}

function addDroneModelEntity (viewer: any, modelId: string, positionProperty: any, orientationProperty: any) {
  viewer.entities.add({
    id: modelId,
    name: '无人机',
    position: positionProperty,
    orientation: orientationProperty,
    model: {
      uri: '/src/assets/images/aircraft.gltf',
      scale: 0.05,
      minimumPixelSize: 0.1,
      maximumScale: 0.6
    }
  })
}

function calculateWaypointPosition (waypoint: any) {
  const cartographic = Cesium.Cartographic.fromCartesian(waypoint.position)
  const adjustedHeight = cartographic.height + 0.11
  const adjustedPosition = new Cesium.Cartographic(cartographic.longitude, cartographic.latitude, adjustedHeight)
  return Cesium.Cartographic.toCartesian(adjustedPosition)
}

function calculateCameraProperties (flightData: any, cameraConfig: any, params: any, properties: any) {
  const { element, curIndex, time, position, completedActionTimeSec, lastPitch, lastZoom } = params
  const { orientationProperty, modelHeadingProperty, frustumHeadingProperty, frustumPitchProperty, mapZoomProperty } = properties

  const defaultZoom = flightData.defaultZoom
  let currentPitch = lastPitch
  let currentZoom = lastZoom
  const prevIndex = curIndex - 1

  let heading = cameraConfig.startPosHeading
  if (curIndex !== 0) {
    heading = flightData.waypointInfo[prevIndex].hpr.heading
  }

  modelHeadingProperty.addSample(time, heading)
  frustumHeadingProperty.addSample(time, heading)
  frustumPitchProperty.addSample(time, currentPitch)

  const zoom = currentZoom === 0 ? defaultZoom : currentZoom
  mapZoomProperty.addSample(time, zoom)

  const orientation = convertToOrientationQuaternion(position, heading)
  orientationProperty.addSample(time, orientation)

  let yawRotationStart
  let currentYaw: any

  for (let actionIndex = 0; actionIndex < element.actionTimes.length; actionIndex++) {
    const action = element.actionTimes[actionIndex]
    processWaypointAction(actionIndex, action, defaultZoom)
  }

  function processWaypointAction (actionIndex: number, action: any, zoomValue: number) {
    let prevActionEndTime: any | undefined
    const prevAction = element.actionTimes[actionIndex - 1]
    let prevActionEnd = prevAction?.endTime ?? element.startTime

    switch (action.type) {
      case WaylineActionType.ROTATE_YAW:
        if (!currentYaw) {
          yawRotationStart = orientation
          currentYaw = heading
          prevActionEnd = dayjs(prevActionEnd).add(0.1, 'second').valueOf()
        }
        if (!yawRotationStart) {
          yawRotationStart = orientation
        }
        if (!prevActionEnd) {
          prevActionEnd = dayjs(prevActionEnd).add(0.1, 'second').valueOf()
        }
        // eslint-disable-next-line no-case-declarations
        const yawStartTime = Cesium.JulianDate.fromDate(new Date(prevActionEnd))
        orientationProperty.addSample(yawStartTime, yawRotationStart) // 添加旋转开始时的朝向
        modelHeadingProperty.addSample(yawStartTime, currentYaw) // 添加模型开始时的航向
        frustumHeadingProperty.addSample(yawStartTime, currentYaw) // 添加视锥开始时的航向

        // eslint-disable-next-line no-case-declarations
        const yawEndTime = Cesium.JulianDate.fromDate(new Date(action.endTime))
        // eslint-disable-next-line no-case-declarations
        const adjustedHeading = Math.floor(action.heading + 90) // 调整航向角度
        // eslint-disable-next-line no-case-declarations
        const endOrientation = convertToOrientationQuaternion(position, adjustedHeading) // 计算结束时的四元数朝向
        orientationProperty.addSample(yawEndTime, endOrientation) // 添加旋转结束时的朝向
        modelHeadingProperty.addSample(yawEndTime, adjustedHeading) // 添加模型结束时的航向
        frustumHeadingProperty.addSample(yawEndTime, adjustedHeading) // 添加视锥结束时的航向
        yawRotationStart = endOrientation // 更新旋转起始朝向
        currentYaw = adjustedHeading // 更新当前航向
        break

      case WaylineActionType.GIMBAL_ROTATE_YAW:
        if (!currentYaw) {
          currentYaw = heading
          prevActionEndTime = dayjs(prevActionEnd).add(0.1, 'second').valueOf()
        }
        if (!prevActionEndTime) {
          prevActionEndTime = dayjs(prevActionEnd).add(0.1, 'second').valueOf()
        }
        // eslint-disable-next-line no-case-declarations
        const gimbalYawStartTime = Cesium.JulianDate.fromDate(new Date(prevActionEndTime!))
        frustumHeadingProperty.addSample(gimbalYawStartTime, currentYaw!) // 添加云台旋转开始时的航向

        // eslint-disable-next-line no-case-declarations
        const gimbalYawEndTime = Cesium.JulianDate.fromDate(new Date(action.endTime))
        // eslint-disable-next-line no-case-declarations
        const gimbalYaw = Math.floor(action.heading + 90) // 计算云台旋转航向
        frustumHeadingProperty.addSample(gimbalYawEndTime, gimbalYaw) // 添加云台旋转结束时的航向
        currentYaw = gimbalYaw // 更新当前航向
        break

      case WaylineActionType.GIMBAL_ROTATE_PITCH:
        if (lastPitch === 0) {
          currentPitch = lastPitch
          prevActionEndTime = dayjs(prevActionEnd).add(0.1, 'second').valueOf()
        }
        if (!prevActionEndTime) {
          prevActionEndTime = dayjs(prevActionEnd).add(0.1, 'second').valueOf()
        }
        // eslint-disable-next-line no-case-declarations
        const pitchStartTime = Cesium.JulianDate.fromDate(new Date(prevActionEndTime!))
        frustumPitchProperty.addSample(pitchStartTime, currentPitch) // 添加云台俯仰开始时的角度

        // eslint-disable-next-line no-case-declarations
        const pitchEndTime = Cesium.JulianDate.fromDate(new Date(action.endTime))
        // eslint-disable-next-line no-case-declarations
        const gimbalPitch = Math.floor(action.gimbalPitchRotateAngle) // 计算云台俯仰角度
        frustumPitchProperty.addSample(pitchEndTime, gimbalPitch) // 添加云台俯仰结束时的角度
        currentPitch = gimbalPitch // 更新当前俯仰角度
        break

      case WaylineActionType.ZOOM:
        if (lastZoom === 0) {
          currentZoom = zoomValue
          prevActionEndTime = dayjs(prevActionEnd).add(0.1, 'second').valueOf()
        }
        if (!prevActionEndTime) {
          prevActionEndTime = dayjs(prevActionEnd).add(0.1, 'second').valueOf()
        }
        // eslint-disable-next-line no-case-declarations
        const zoomStartTime = Cesium.JulianDate.fromDate(new Date(prevActionEndTime!))
        mapZoomProperty.addSample(zoomStartTime, currentZoom) // 添加变焦开始时的焦距

        // eslint-disable-next-line no-case-declarations
        const zoomEndTime = Cesium.JulianDate.fromDate(new Date(action.endTime))
        // eslint-disable-next-line no-case-declarations
        const focalLength = Math.floor(action.focalLength) // 计算焦距
        mapZoomProperty.addSample(zoomEndTime, focalLength) // 添加变焦结束时的焦距
        currentZoom = focalLength // 更新当前焦距
        break
    }
  }

  const finalTime = dayjs(element.startTime).add(completedActionTimeSec + 0.5, 'second').valueOf()
  const finalJulianTime = Cesium.JulianDate.fromDate(new Date(finalTime))
  const finalOrientation = convertToOrientationQuaternion(position, element.hpr.heading)
  orientationProperty.addSample(finalJulianTime, finalOrientation)
  modelHeadingProperty.addSample(finalJulianTime, element.hpr.heading)
  frustumHeadingProperty.addSample(finalJulianTime, element.hpr.heading)
  frustumPitchProperty.addSample(finalJulianTime, currentPitch)

  const finalZoom = currentZoom === 0 ? defaultZoom : currentZoom
  mapZoomProperty.addSample(finalJulianTime, finalZoom)

  return {
    pitch: currentPitch,
    zoom: currentZoom
  }
}

export function convertToOrientationQuaternion (position: any, headingDegrees: number) {
  const headingPitchRoll = Cesium.HeadingPitchRoll.fromDegrees(headingDegrees, 0, 0)
  return Cesium.Transforms.headingPitchRollQuaternion(position, headingPitchRoll)
}

export function setFrustumObj (view, position, hpr) {
  const frustum = new CameraFrustumVisualizer({
    position: position,
    heading: hpr.heading,
    pitch: hpr.pitch,
    roll: hpr.roll,
    fov: hpr.fov,
    near: 1,
    far: 200,
    aspectRatio: 0.75,
    viewer: view,
    color: [0, 201, 136]
  })
  return frustum
}

/** 获取长度和时长 */
export const getTimeDistance = () => {
  if (store.state.editRouteList.length === 0) {
    return { distance: 0, timeFormat: 0, timeSec: 0 }
  }
  let distance_len = 0
  const timeObj = {
    hoverTime: 0,
    panoShotTimes: 0,
    startRecordTimes: 0,
    zoomTimes: 0,
    psdkTimes: 0,
    rotateYawTimes: 0,
    gimbalRotateYawTimes: 0,
    gimbalRotatePitchTimes: 0,
    takePhotoTimes: 0
  }
  let length = 0
  store.state.editRouteList.forEach((item, index) => {
    item.eventList.forEach(row => {
      if (row.type === 'hover') { timeObj.hoverTime += row.hoverTime * 1 }
      if (row.type === WaylineActionType.PANOSHOT) { timeObj.panoShotTimes += 50 }
      if (row.type === WaylineActionType.TAKE_PHOTO) { timeObj.takePhotoTimes += 1 }
      if (row.type === WaylineActionType.ROTATE_YAW) { timeObj.rotateYawTimes += 2 }
      if (row.type === WaylineActionType.GIMBAL_ROTATE_YAW) { timeObj.gimbalRotateYawTimes += 2 }
      if (row.type === WaylineActionType.GIMBAL_ROTATE_PITCH) { timeObj.gimbalRotatePitchTimes += 1 }
      if (row.type === WaylineActionType.START_RECORD) { timeObj.startRecordTimes += 1 }
      if (row.type === WaylineActionType.ZOOM && row.focalLength !== length) {
        timeObj.zoomTimes += 3
        length = row.focalLength
      }
      if (row.type === WaylineActionType.MEGAPHONE || row.type === WaylineActionType.MEGAPHONE_STOP || row.type === WaylineActionType.SEARCH_LIGHT || row.type === WaylineActionType.SEARCH_LIGHT_STOP) { timeObj.psdkTimes += 1 }
    })
    index !== store.state.editRouteList.length - 1 && (distance_len += Cesium.Cartesian3.distance(item.position, store.state.editRouteList[index + 1].position))
  }
  )
  const H = getRouterTime(store.state.editRouteList, timeObj, store.state.editRouteList.length)

  const obj = {
    distance: distance_len.toFixed(1),
    ...H
  }
  return obj
}

/**
 * 根据航线ID解析起飞点和航点列表
 * @param waylineId 航线ID
 * @param workspaceId 工作空间ID
 * @returns 解析后的数据
 */
export async function parseWaylinePoints (
  workspaceId: string,
  waylineId: string

): Promise<ParsedWaylineData> {
  // 1. 下载航线文件
  const res = await downloadWaylineFile(workspaceId, waylineId)
  if (!res) {
    throw new Error('下载航线文件失败')
  }

  // 2. 解压 KMZ，提取 KML
  const data = new Blob([res], { type: 'application/zip' })
  const zip = await JSZip.loadAsync(data)
  const kmlFile = Object.values(zip.files).find(file => file.name.endsWith('.kml'))
  if (!kmlFile) {
    throw new Error('KMZ 中未找到 KML 文件')
  }
  const kmlText = await kmlFile.async('text')
  let kmlDomObject = DJIWaylineTools.xmlToObject(kmlText)

  // 3. 规范化 Document 结构
  if (!kmlDomObject.Document) {
    const obj: any = { Document: {}, attributes: {} }
    obj.attributes = kmlDomObject.attributes
    for (const key in kmlDomObject) {
      if (key !== 'attributes') {
        obj.Document[key] = kmlDomObject[key]
      }
    }
    kmlDomObject = obj
  }
  if (kmlDomObject.Document.templateInfo) {
    kmlDomObject.Document = { ...kmlDomObject.Document.templateInfo.Document }
  }

  const missionConfig = kmlDomObject.Document.missionConfig || {}
  const folders = kmlDomObject.Document.Folder
  const firstFolder = Array.isArray(folders) ? folders[0] : folders

  // 4. 解析起飞点
  let takeOffPoint: TakeOffPoint | null = null
  const takeOffRefRaw = missionConfig.takeOffRefPoint
  if (takeOffRefRaw && takeOffRefRaw !== '0,0,0' && takeOffRefRaw !== 'null') {
    if (typeof takeOffRefRaw === 'string') {
      const parts = takeOffRefRaw.split(',')
      // 注意：KML 中可能是 "经度,纬度,高度" 或 "纬度,经度,高度"，根据实际调整
      let lng = parseFloat(parts[0])
      let lat = parseFloat(parts[1])
      const alt = parseFloat(parts[2] || '0')
      // 简单校验：若纬度超出范围，则交换
      if (lat > 90 || lat < -90) {
        const temp = lng
        lng = lat
        lat = temp
      }
      takeOffPoint = { longitude: lng, latitude: lat, altitude: alt }
    } else if (typeof takeOffRefRaw === 'object') {
      let { longitude, latitude, height, altitude } = takeOffRefRaw
      if (latitude > 90 || latitude < -90) {
        const temp = latitude
        latitude = longitude
        longitude = temp
      }
      takeOffPoint = {
        longitude: parseFloat(longitude) || 0,
        latitude: parseFloat(latitude) || 0,
        altitude: parseFloat(height ?? altitude ?? 0)
      }
    }
  }

  // 5. 解析航点
  const waypoints: Waypoint[] = []
  const placemarks = firstFolder?.Placemark
  if (placemarks) {
    const placemarkArray = Array.isArray(placemarks) ? placemarks : [placemarks]
    for (const pm of placemarkArray) {
      let pointCoords: { longitude: number; latitude: number; height?: number } | null = null
      const rawPoint = pm.Point
      if (!rawPoint) continue

      // 处理不同类型的 Point 结构
      if (typeof rawPoint === 'string') {
        const parts = rawPoint.split(',')
        let lng = parseFloat(parts[0])
        let lat = parseFloat(parts[1])
        const h = parseFloat(parts[2] || '0')
        if (lat > 90 || lat < -90) {
          const temp = lng
          lng = lat
          lat = temp
        }
        pointCoords = { longitude: lng, latitude: lat, height: h }
      } else if (rawPoint.coordinates) {
        const coords = rawPoint.coordinates
        if (typeof coords === 'string') {
          const parts = coords.split(',')
          let lng = parseFloat(parts[0])
          let lat = parseFloat(parts[1])
          const h = parseFloat(parts[2] || '0')
          if (lat > 90 || lat < -90) {
            const temp = lng
            lng = lat
            lat = temp
          }
          pointCoords = { longitude: lng, latitude: lat, height: h }
        } else {
          // coords 是对象
          let lng = parseFloat(coords.longitude)
          let lat = parseFloat(coords.latitude)
          const h = parseFloat(coords.height ?? coords.altitude ?? 0)
          if (lat > 90 || lat < -90) {
            const temp = lng
            lng = lat
            lat = temp
          }
          pointCoords = { longitude: lng, latitude: lat, height: h }
        }
      } else if (rawPoint.longitude !== undefined) {
        let lng = parseFloat(rawPoint.longitude)
        let lat = parseFloat(rawPoint.latitude)
        const h = parseFloat(rawPoint.height ?? rawPoint.altitude ?? 0)
        if (lat > 90 || lat < -90) {
          const temp = lng
          lng = lat
          lat = temp
        }
        pointCoords = { longitude: lng, latitude: lat, height: h }
      }

      if (pointCoords) {
        // 获取航点高度：优先使用 pm.height，否则使用 Point 中的 height
        const height = pm.height !== undefined ? parseFloat(pm.height) : pointCoords.height ?? 0
        waypoints.push({
          longitude: pointCoords.longitude,
          latitude: pointCoords.latitude,
          height: height
        })
      }
    }
  }

  return { takeOffPoint, waypoints }
}
/** 航点航线-航点前插入航点 */
export const handleAddAfterPoint = (point: any, posotion: Cesium.Cartesian2) => {
  if (!point) return
  let nowPoint
  const index = store.state.currentRoutePosIndex
  store.state.currentRoutePosIndex > 1 ? nowPoint = store.state.editRouteList[store.state.currentRoutePosIndex - 2].position : nowPoint = store.state.routeStartPosition
  const o = getPointPosition(nowPoint, store.state.editRouteList[store.state.currentRoutePosIndex].position)
  globalType.value = Number(Cesium.Math.toDegrees(o.h).toFixed(1))
  globalType.value = Number(globalType.value.toFixed(1))
  let flag = false
  if (currentSettingData.heightMode === WaylineMapType.ABOVE_GROUND_LEVEL) {
    flag = true
  }
  const frustumOption = {
    heading: 0,
    pitch: 0,
    roll: 0,
  }
  frustumOption.heading = Math.floor(
    Cesium.Math.toDegrees(o.h) + 90,
  )
  frustumOption.pitch = 90
  frustumOption.roll = 0
  const currentPos = Cesium.Cartesian3.fromDegrees(point.longitude, point.latitude, point.height)
  state.drawRouteTool?.insertWaypointAndRebuild(index, currentPos, frustumOption, flag, () => {
    store.state.currentRoutePosIndex = index
    showFrustum(store.state.editRouteList[index])
  })
  isSave.value = true
}
/** 航点航线-航点后插入航点 */
export const handleAddBeforePoint = (point: any, position: Cesium.Cartesian2) => {
  if (!point) return
  const index = store.state.currentRoutePosIndex + 1
  const nowPoint = store.state.editRouteList[store.state.currentRoutePosIndex - 1] ? store.state.editRouteList[store.state.currentRoutePosIndex - 1].position : store.state.routeStartPosition
  const hprPos = getPointPosition(nowPoint, store.state.editRouteList[store.state.currentRoutePosIndex].position)
  const frustumOption = {
    heading: 0,
    pitch: 0,
    roll: 0,
  }
  frustumOption.heading = Math.floor(
    Cesium.Math.toDegrees(hprPos.h) + 90,
  )
  frustumOption.pitch = 90
  frustumOption.roll = 0
  const currentPos = Cesium.Cartesian3.fromDegrees(point.longitude, point.latitude, point.height)
  let flag = false
  if (currentSettingData.heightMode === WaylineMapType.ABOVE_GROUND_LEVEL) {
    flag = true
  }

  state.drawRouteTool?.insertWaypointAndRebuild(index, currentPos, frustumOption, flag, () => {
    store.state.currentRoutePosIndex = index
    showFrustum(store.state.editRouteList[index])
  })
  isSave.value = true
}
/** 航点航线-在最后航点添加航点 */
export const handleAddLastPoint = (point: any, position: Cesium.Cartesian2) => {
  if (!point) return
  const index = store.state.editRouteList.length
  const nowPoint = store.state.editRouteList.length - 2 >= 0 ? store.state.editRouteList[store.state.editRouteList.length - 2].position : store.state.routeStartPosition
  const hprPos = getPointPosition(nowPoint, store.state.editRouteList[store.state.editRouteList.length - 1].position)
  const frustumOption = {
    heading: 0,
    pitch: 0,
    roll: 0,
  }
  frustumOption.heading = Math.floor(
    Cesium.Math.toDegrees(hprPos.h) + 90,
  )
  frustumOption.pitch = 90
  frustumOption.roll = 0
  const currentPos = Cesium.Cartesian3.fromDegrees(point.longitude, point.latitude, point.height)
  let flag = false
  if (currentSettingData.heightMode === WaylineMapType.ABOVE_GROUND_LEVEL) {
    flag = true
  }
  state.drawRouteTool?.insertWaypointAndRebuild(index, currentPos, frustumOption, flag, () => {
    store.state.currentRoutePosIndex = index
    showFrustum(store.state.editRouteList[index])
  })
  isSave.value = true
}

/** 航点航线-飞到此处 */
export const handleFlyPont = (point: any, posotion: Cesium.Cartesian2) => {
  if (!point) return
  state.drawRouteTool?.flyPointLine(point, posotion)
}

export const handleDeletePoint = (point: any) => {
  if (!point) return
  state.drawRouteTool?.deletePoint(point)
}
